/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Box, Drawer, Typography } from '@mui/material';
import {
  formatFollowUpStateLabel,
  incidentStateExplanatoryNote,
  formatEdgeLiveConditionLabel,
} from '../../utils/incidentStateLabels';
import IncidentReviewerActionPanel from './IncidentReviewerActionPanel';
import RecurringRiskHeatmapCard from './RecurringRiskHeatmapCard';
import {
  incidentDisplaySummaryForRow,
  incidentInsightChipsForRow,
  incidentPolicyContextForRow,
  operatorObservationDrawerTitle,
} from '../../pages/incidents/incidentDisplayModel';
import { buildZonesByIdMap, presentPackPolicyForIncidentLike } from '../../pages/analytics/packCoverageModel';
import { presentPolicy } from '../../domain/policies/policyPresentation';
import { buildReportContextForAlias, readPolicyUiContext } from '../../domain/policies/policyPacks';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import { resolvePatternEvidenceZoneLabel } from '../../utils/customerPlaceLabel';
import { auditApi } from '../../services/auditApi';
import {
  incidentsApi,
  type Incident,
  type IncidentGroupHeatmapResponse,
  type IncidentReviewOutcomeRollupResponse,
} from '../../services/incidentsApi';
import { policiesApi } from '../../services/policiesApi';
import type { WorkflowCorrectiveAction } from '../../services/reportsApi';
import { incidentCanonicalEvidenceReadinessLabel } from './reviewCanonical';
import { computeIncidentClipTiming } from './computeIncidentClipTiming';
import {
  displayPatternEvidenceProofLabel,
  displayPatternWorkflowLabel,
} from '../../domain/incidents/patternReviewDisplayLabels';
import {
  getIncidentGroupPatternLabel,
  getIncidentGroupPatternSummary,
  isIncidentGroupRecurring,
} from '../../domain/incidents/incidentPatternSemantics';
import type { IncidentGroup } from '../../services/incidentsApi';
import { readPatternAssignedByLabel } from '../../domain/incidents/incidentOwnershipSemantics';
import { resolveHonestClipStatus } from '../../domain/incidents/evidenceClipPresence';
import {
  AuditScopeBadge,
  deriveGroupedProgressSummary,
  GroupedIncidentHeatmapSection,
  GroupedRiskPatternSummary,
  GroupedIncidentReviewWorkspace,
  PatternDecisionCard,
} from './GroupedIncidentReview';
import { GroupedUnderstandRiskPanel } from './GroupedUnderstandRiskPanel';
import {
  buildRecurringRiskSummaryLine,
  UNDERSTAND_RISK_HEATMAP_HELPER,
  UNDERSTAND_RISK_HEATMAP_TITLE,
} from '../../domain/incidents/groupedUnderstandRiskPresentation';
import type { GroupedIncidentGuidedWorkflowProps } from './GroupedIncidentGuidedWorkflow';
import {
  groupedCorrectiveActionLocked,
  isExplicitRepresentativeReviewComplete,
  readRepresentativeReviewOutcome,
  type GroupedGuidedPanelId,
} from '../../domain/incidents/groupedGuidedWorkflow';
import { deriveGroupedCorrectiveWorkflowState } from '../../domain/incidents/groupedCorrectiveActionWorkflow';
import { readImpactMeasurementWaived } from '../../domain/incidents/trackImpactSemantics';
import {
  buildPatternDispositionSummaryForPatternCase,
} from '../../domain/incidents/patternDispositionSummary';
import { PatternDispositionSummaryCard } from './PatternDispositionSummaryCard';
import {
  buildGroupedReportReadinessPresentation,
  buildGroupedReportSignOffMissingItems,
  buildGroupedEvidenceGateMissingItems,
  buildCanonicalClaimReadinessMissingItems,
  mergeGroupedSignOffMissingItems,
  isCanonicalClaimControlResolved,
} from '../../domain/incidents/groupedSafetyCaseReportReadiness';
import {
  clearGroupedReviewUiSnapshot,
  formatGroupedReviewLocalViewHint,
  loadGroupedReviewUiSnapshot,
  readLegacyRiskContextViewedAt,
  resolveGroupedGuidedInitialPanelId,
  saveGroupedReviewUiSnapshot,
  saveGroupedReviewResumeSnapshot,
} from '../../domain/incidents/groupedReviewUiPreferences';
import {
  GROUPED_INCIDENT_GROUPING_BASIS_FALLBACK,
  normalizeGroupingBasis,
  ownerRoleIsUnassigned,
  patternBucketSummaryText,
} from '../../domain/incidents/incidentPatternVocabulary';
import { GroupedAssignActionPanel } from './GroupedAssignActionPanel';
import {
  isExplicitFollowUpMeasurementRecorded,
  isTrackImpactMeasurementReady,
} from '../../domain/incidents/trackImpactSemantics';
import { groupedStepTitle } from '../../domain/incidents/groupedRepresentativeAuditCopy';
import {
  isObservationDispositionMode,
  isPatternCaseReviewMode,
  resolveIncidentReviewSurfaceMode,
  type IncidentReviewSurfaceMode,
} from '../../domain/incidents/incidentReviewSurfaceMode';
import { AfterSignedScanReviewPanel } from './AfterSignedScanReviewPanel';
import { RiskPatternWorkflowReviewContextPanel, type RiskPatternWorkflowReviewActions, type RiskPatternWorkflowReviewTimelineProps } from './RiskPatternWorkflowReviewContextPanel';
import type { AfterSignedScanReviewContext } from '../../domain/incidents/afterSignedScanReview';
import {
  formatReviewWindowHeaderValue,
  resolveReviewWindowContext,
} from '../../domain/incidents/reviewWindowContext';
import type { IncidentTimeRange } from '../../pages/incidents/useIncidentsTimeRange';
import {
  resolveRiskPatternReviewPageState,
  type RiskPatternWorkflowReviewContext,
} from '../../domain/incidents/riskPatternWorkflowReviewContext';
import { buildSafetyCaseVerifyPath } from '../../domain/safetyCases/investigateControlLink';
import {
  GROUPED_REPRESENTATIVE_EVENT_HELPER,
} from '../../domain/incidents/groupedReviewWorkflowUx';
import {
  buildPatternOwnedNextActionCopy,
  buildPatternOwnedWorkflowProgress,
  patternOwnedCurrentStageLabel,
  patternOwnedNextActionButtons,
  patternOwnershipHasLinkedCase,
} from '../../domain/incidents/patternOwnershipContainment';
import {
  bindPatternReviewActionsToScanOrEvidence,
  omitDuplicatePatternReviewCtas,
  readPatternReviewPrimaryHref,
  resolvePatternReviewPrimaryHandler,
  resolvePatternReviewPrimaryHref,
} from '../../domain/incidents/patternReviewScanDeepLink';
import { useSiteTimeZone } from '../../hooks/useSiteTimeZone';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import { buildGroupedIncidentReviewModel } from './groupedIncidentReviewModel';
import {
  JOURNEY_PROGRESS_STORAGE_PREFIX,
  EVIDENCE_REVIEW_STEP_ID,
  buildJourneyEvidenceFingerprint,
  createReviewSessionId,
  normalizeJourneyFingerprintText,
  type JourneyPhaseId,
  type JourneyProgressSnapshot,
  type JourneyStepId,
} from './incidentDrawerJourneyProgress';
import IncidentDrawerStepNavigation from './incidentDrawerStepNavigation';
import IncidentDrawerDetailContent, { type IncidentDrawerDetailScope } from './IncidentDrawerDetailContent';
import { useIncidentEvidenceMoment } from './useIncidentEvidenceMoment';
import {
  buildReviewerImageDrawerContext,
  extractIncidentForkliftConfidence,
  extractReviewMediaConfidence,
  formatForkliftConfidenceLabel,
  formatPersonConfidenceLabel,
  formatSnapshotConfidenceLabel,
  forkliftSubjectMetadataChipLabel as resolveForkliftSubjectMetadataChipLabel,
} from '../../domain/incidents/reviewerImagePolicy';
import {
  observationBackToCaseButtonLabel,
  resolveNextObservationIncidentId,
  resolveParentCaseIncidentId,
} from '../../domain/incidents/observationDispositionNextStep';

export type GroupedHotspotOperationalMetrics = {
  qualifyingEntryCount: number | null;
  occupancyDurationMs: number | null;
  forkliftCoPresenceEntryCount: number | null;
};

function knownNonnegativeNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function deriveGroupedHotspotOperationalMetrics(args: {
  observationWindowCount: number;
  group?: IncidentGroup | null;
  incidents?: Incident[] | null;
}): GroupedHotspotOperationalMetrics {
  const incidents = Array.isArray(args.incidents) ? args.incidents : [];
  const completeIncidentScope = args.observationWindowCount > 0
    && incidents.length === args.observationWindowCount;
  const sumKnownIncidentMetric = (read: (incident: Incident) => unknown): number | null => {
    if (!completeIncidentScope) return null;
    const values = incidents.map((incident) => knownNonnegativeNumber(read(incident)));
    let total = 0;
    for (const value of values) {
      if (value == null) return null;
      total += value;
    }
    return total;
  };
  const group = args.group;

  return {
    qualifyingEntryCount:
      knownNonnegativeNumber(group?.qualifying_entry_count)
      ?? sumKnownIncidentMetric((incident) => incident.episode_count),
    occupancyDurationMs:
      knownNonnegativeNumber(group?.restricted_zone_occupancy_duration_ms)
      ?? sumKnownIncidentMetric((incident) => incident.restricted_zone_occupancy_duration_ms),
    forkliftCoPresenceEntryCount:
      knownNonnegativeNumber(group?.forklift_co_presence_entry_count)
      ?? sumKnownIncidentMetric((incident) => incident.forklift_co_presence_entry_count),
  };
}

/**
 * Shared incident review surface used by drawer triage and full-page review.
 * Receives all needed state, handlers, and helpers through a context bag.
 */
export interface IncidentDrawerProps {
  ctx: Record<string, any>;
}

const IncidentDrawer: React.FC<IncidentDrawerProps> = ({ ctx }) => {
  const {
    // Drawer state
    viewDialogOpen, setViewDialogOpen,
    selectedIncident, selectedGroupContext,
    renderMode = 'drawer', reviewFocus = '', onOpenReviewPage, onOpenReviewPageFullProof,
    drawerAdvancedOpen, setDrawerAdvancedOpen,
    drawerIncidentLoading, drawerBusy,
    workflowFeedback, clearWorkflowFeedback: _clearWorkflowFeedback,
    // Activity & comments
    incidentActivityItems, incidentActivityLoading, incidentActivityError,
    incidentComments, incidentCommentsLoading, incidentCommentsError,
    incidentSafetyEpisodes, incidentSafetyEpisodesLoading, incidentSafetyEpisodesError,
    incidentSafetyEpisodesAvailable,
    // Snapshot
    latestSnapshotObjectUrl, latestSnapshotLoading, latestSnapshotError,
    latestVideoObjectUrl, latestVideoLoading, latestVideoError,
    latestVideoIdentity, loadEvidenceMoment,
    // Ops drafts
    opsStateDraft, setOpsStateDraft,
    opsAssigneeDraft, setOpsAssigneeDraft,
    opsTeamDraft, setOpsTeamDraft,
    opsSlaTargetDraft, setOpsSlaTargetDraft,
    measurementBaselineDraft, setMeasurementBaselineDraft,
    measurementFollowUpDraft, setMeasurementFollowUpDraft,
    measurementMeasuredAtDraft, setMeasurementMeasuredAtDraft,
    measurementOutcomeStatusDraft, setMeasurementOutcomeStatusDraft,
    measurementNoteDraft, setMeasurementNoteDraft,
    measurementImpactWaivedDraft, setMeasurementImpactWaivedDraft,
    correctiveActionTypeDraft, setCorrectiveActionTypeDraft,
    correctiveActionPriorityDraft, setCorrectiveActionPriorityDraft,
    correctiveActionDueDateDraft, setCorrectiveActionDueDateDraft,
    correctiveActionVerificationMethodDraft, setCorrectiveActionVerificationMethodDraft,
    correctiveActionControlReviewDraft, setCorrectiveActionControlReviewDraft,
    workflowId,
    workflowCorrectiveActions, workflowImpactSummary, workflowClaimReadiness,
    workflowFieldVerification, fieldVerificationBusy, handleAnswerFieldVerificationItem,
    refreshWorkflowCorrectiveSummary,
    handleAddWorkflowEvidenceNote, handleUpdateWorkflowActionStatus,
    handleSaveCoverageReviewAttestation, handleSaveZoneControlsConfirmation, claimReadinessRefreshKey,
    // Form drafts
    noteDraft, setNoteDraft,
    severityDraft, setSeverityDraft,
    // Suppression state
    suppressionsLoading, suppressionsError,
    _edgeSuppressions, matchingSuppressions,
    ppeMissingZoneDiag, ppeMissingZoneDiagLoading, ppeMissingZoneDiagError,
    // Runbooks
    runbookTemplates, incidentRunbookBusy, setIncidentRunbookBusy,
    // Action handlers
    _refreshSelectedIncident,
    handleAcknowledge, handleMarkInReview,
    handleAddCommentShortcut, handleSaveNote, handleSaveOps, handleSaveFollowupOwner, handleSaveMeasurement, handleSaveCorrectiveAction,
    _handleSaveSeverity, handleOverrideSeverity,
    handleMarkFalsePositive, openResolveDialogWithOutcome, handleExportEvidence,
    handleOpenThresholdTuning, handleOpenPolicyEditor,
    handleClearSuppressionForIncident,
    handleEnsureWorkOrder, handleSyncToOdoo, handleSyncFromOdoo,
    _handleRunIncidentRunbook,
    _loadEdgeSuppressions, _loadPpeMissingZoneDiag,
    // Evidence helpers
    fetchEvidenceFileBlob, fetchLatestSnapshotBlob, fetchLatestVideoBlob, downloadBlob,
    _bestSnapshotAttachment, _bestVideoAttachment, _overlaySnapshotBlob,
    // Label & presentation helpers
    _humanLabelCompact, _incidentTypeLabel, _findingForIncident,
    _severityChipSx, _severityIcon, _opsStateLabel,
    _ppeMissingItemsFromIncident, _ppeSnapshotItemsFromIncident,
    _ppeStabilizedStateFromIncident, _ppeItemsSummary,
    
    getOdooStateColor,
    derivePolicyNotificationRoutes,
    buildContextBundle,
    // Capabilities
    canManageIncidentWorkflow, canCommentOnIncident,
    canOverrideIncidentSeverity, canExportIncidentEvidence,
    canAdjustIncidentThreshold, canOpenAuditTrail, canOpenCopilot,
    canRunIncidentRunbooks, canManageIncidentSuppressions, canUseOpsAutomation,
    _isAdvancedIncidentsUi,
    // Navigation & context
    navigate, activeScope, commentInputRef,
    setSuppressDialogOpen, setCloseDialogOpen, _setCloseReason,
    IncidentEvidenceAttachmentPreview, PolicyNotificationStatusChip,
    deriveLifecycle, _edgeSuppressionsByDevice,
    executeRunbookServer,
    scopedSiteId, user, opsStateResult,
    allPolicies,
    allZones,
    zoneNameById, cameraNameById,
    incidentRowTypeLabel,
    setTableMode, _setFilterOpsQueue,
    setFilterIncidentType, _setFilterSeverity, setFilterCameraId, setFilterZoneId, setFilterDeviceId,
    _fetchData, _titleCase,
    _openReportDialog,
    onGroupPatternAction,
    canManageGroupPatterns = false,
    groupPatternActionBusy = false,
    onGroupedHeatmapCellSelect,
    groupedHeatmapCellSelectCaption,
    reviewTimeRange,
    reviewExactMode = false,
    reviewSurfaceMode: reviewSurfaceModeFromParent = null as IncidentReviewSurfaceMode | null,
    reviewHeatmapHourActive = false,
    riskCategoryReportLink = null,
    heatmapOpening = false,
    heatmapPageFeedback,
    heatmapViewingIncidentId = null,
    onHeatmapWindowChange,
    onViewPatternOccurrences,
    registerOccurrenceOpenedHandler,
    afterSignedScanReview = null as AfterSignedScanReviewContext | null,
    afterSignedScanScopedActivityCount = null as number | null,
    afterSignedScanLatestObservation = null as string | null,
    reviewWindowContext: reviewWindowContextFromParent = null as import('../../domain/incidents/reviewWindowContext').ReviewWindowContext | null,
    onReviewFullSafetyCaseHistory,
    onAfterSignedScanTimelineChange,
    onResolveAfterSignedScanStartBeforeSignedScan,
    afterSignedScanTimeline = null as RiskPatternWorkflowReviewTimelineProps | null,
    riskPatternWorkflowReviewContext = null as RiskPatternWorkflowReviewContext | null,
    riskPatternWorkflowReviewLoading = false,
    riskPatternWorkflowReviewError = null as string | null,
    riskPatternWorkflowReviewActions = undefined as RiskPatternWorkflowReviewActions | undefined,
    observationParentCaseCount = 0,
    observationReviewQueueIncidents = [] as import('../../services/incidentsApi').Incident[],
    patternScopeIncidents = [] as import('../../services/incidentsApi').Incident[],
  } = ctx;
  const isPageMode = renderMode === 'page';
  const siteTimeZone = useSiteTimeZone(String(scopedSiteId || selectedIncident?.site_id || '').trim() || null);

  const policiesForPresentation = Array.isArray(allPolicies) ? allPolicies : [];
  const drawerScrollRef = React.useRef<HTMLDivElement | null>(null);
  const reviewShellRef = React.useRef<HTMLDivElement | null>(null);
  const journeyRailRef = React.useRef<HTMLDivElement | null>(null);
  const { evidencePlayerCardRef, evidencePlayerRef, evidenceMomentError,
    handleEvidencePlayerError, handleViewEvidenceMoment,
    seekLoadedEvidenceMoment } = useIncidentEvidenceMoment(
    { latestVideoIdentity, latestVideoObjectUrl, loadEvidenceMoment },
  );
  const zonesByIdForPack = React.useMemo(() => buildZonesByIdMap(Array.isArray(allZones) ? allZones : []), [allZones]);
  const packPolicyHeader = React.useMemo(() => {
    const row = selectedIncident || selectedGroupContext;
    return presentPackPolicyForIncidentLike(row as any, policiesForPresentation, { zonesById: zonesByIdForPack });
  }, [selectedIncident, selectedGroupContext, policiesForPresentation, zonesByIdForPack]);
  const matchedPolicy = React.useMemo(() => {
    const row = (selectedIncident || selectedGroupContext) as any;
    const policyId = String(row?.policy_id || row?.triggered_by?.policy_id || '').trim();
    if (!policyId) return null;
    return policiesForPresentation.find((policy) => String(policy?.policy_id || '').trim() === policyId) || null;
  }, [policiesForPresentation, selectedGroupContext, selectedIncident]);
  const matchedPresentedPolicy = React.useMemo(
    () => (matchedPolicy ? presentPolicy(matchedPolicy, { zonesById: zonesByIdForPack }) : null),
    [matchedPolicy, zonesByIdForPack],
  );
  const matchedPolicyContext = React.useMemo(() => {
    if (!matchedPolicy) return null;
    const directContext = matchedPolicy?.config?.report_context;
    if (directContext && typeof directContext === 'object') return directContext as Record<string, any>;
    const uiContext = readPolicyUiContext(matchedPolicy);
    const alias = String(uiContext?.policy_alias || matchedPresentedPolicy?.alias || '').trim();
    return alias ? buildReportContextForAlias(alias) : null;
  }, [matchedPolicy, matchedPresentedPolicy]);
  const buyerDetectionRuleLabel = String(
    matchedPresentedPolicy?.policyLabel || packPolicyHeader.policyLabel || '',
  ).trim();
  const buyerScanMeaning = String(matchedPolicyContext?.hazard_exposure_label || '').trim();

  const zoneN = (z?: string | null) => (z && zoneNameById?.get?.(String(z))) || z || '—';
  const camN = (c?: string | null) => (c && cameraNameById?.get?.(String(c))) || c || '—';
  const safeText = (value: unknown) => String(value || '').trim();
  const presentBuyerZoneLabel = (value: unknown) => {
    const raw = safeText(value);
    if (!raw) return '';
    return resolvePatternEvidenceZoneLabel({ zoneId: raw, zoneName: zoneNameById?.get?.(raw) || raw });
  };
  const incidentTypeKey = safeText((selectedIncident as any)?.incident_type).toLowerCase();
  const isPpeIncident =
    incidentTypeKey === 'ppe_violation' ||
    incidentTypeKey === 'ppe_compliance' ||
    incidentTypeKey === 'ppe_violation_person' ||
    incidentTypeKey.startsWith('ppe_');
  const titleCaseWords = (value: string) =>
    String(value || '')
      .trim()
      .replace(/[_-]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  const safeWindowScrollTo = React.useCallback((top: number, behavior: ScrollBehavior) => {
    if (typeof window === 'undefined') return;
    if (/jsdom/i.test(String(window.navigator?.userAgent || ''))) return;
    try {
      window.scrollTo({ top, behavior });
    } catch {
      // jsdom does not implement window.scrollTo; browsers do.
    }
  }, []);
  const selectedZoneName = selectedIncident ? String(zoneN(selectedIncident.zone_id)) : null;
  const selectedCameraName = selectedIncident ? String(camN(selectedIncident.camera_id)) : null;
  const selectedGroupZoneName = (selectedGroupContext as any)?.zone_id ? String(zoneN((selectedGroupContext as any).zone_id)) : null;
  const selectedGroupCameraName = (selectedGroupContext as any)?.camera_id ? String(camN((selectedGroupContext as any).camera_id)) : null;

  const currentTitle = operatorObservationDrawerTitle({
    policyLabel: buyerDetectionRuleLabel,
    row: (selectedIncident || selectedGroupContext) as any,
    fallback: selectedIncident ? 'Observation' : (selectedGroupContext ? 'Recurring risk pattern' : 'Review item detail'),
  });

  const workflowLabel = selectedIncident ? deriveLifecycle(selectedIncident as any) : '';
  const opsStateLabel = formatFollowUpStateLabel(
    (selectedIncident as any)?.ops_state || 'new',
    selectedIncident?.status,
  );
  const evidenceLatest = selectedIncident?.evidence?.latest as any;
  const evidenceAttachments = (selectedIncident?.evidence?.attachments || []) as any[];
  const normalizeTimingMs = React.useCallback((numeric: number): number => {
    if (numeric > 1e15) return Math.round(numeric / 1e6);
    if (numeric > 1e12) return Math.round(numeric);
    if (numeric > 1e9) return Math.round(numeric * 1000);
    return Math.round(numeric);
  }, []);
  const parseTimingMs = React.useCallback((value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return normalizeTimingMs(value);
    }
    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) return null;
      if (/^\d+(\.\d+)?$/.test(text)) {
        const numeric = Number(text);
        if (Number.isFinite(numeric) && numeric > 0) return normalizeTimingMs(numeric);
      }
      const parsed = Date.parse(text);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  }, [normalizeTimingMs]);
  const formatTimingInstant = React.useCallback((value: number | null | undefined): string => {
    if (!value || !Number.isFinite(value) || value <= 0) return '—';
    try {
      return formatSiteDateTime(new Date(value).toISOString(), siteTimeZone) || '—';
    } catch {
      return '—';
    }
  }, [siteTimeZone]);
  const formatTimingDelta = React.useCallback((value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    const abs = Math.abs(value);
    if (abs < 1000) return `${value >= 0 ? '+' : '-'}${Math.round(abs)} ms`;
    if (abs < 60000) return `${value >= 0 ? '+' : '-'}${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)} s`;
    return `${value >= 0 ? '+' : '-'}${(abs / 60000).toFixed(abs >= 600000 ? 0 : 1)} min`;
  }, []);
  const formatAgeSeconds = React.useCallback((value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return '—';
    const seconds = Math.round(value);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round((minutes / 60) * 10) / 10;
    if (hours < 48) return `${hours}h`;
    const days = Math.round((hours / 24) * 10) / 10;
    return `${days}d`;
  }, []);
  const parseEvidenceFilenameMs = React.useCallback((filename?: string | null): number | null => {
    const text = String(filename || '').trim();
    if (!text) return null;
    const match = text.match(/(\d{8})_(\d{6})/);
    if (!match) return null;
    const stamp = `${match[1]}T${match[2]}`;
    const iso = `${stamp.slice(0, 4)}-${stamp.slice(4, 6)}-${stamp.slice(6, 8)}T${stamp.slice(9, 11)}:${stamp.slice(11, 13)}:${stamp.slice(13, 15)}Z`;
    const parsed = Date.parse(iso);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);
  const evidenceAttachmentByFilename = React.useCallback(
    (filename?: string | null) => {
      const wanted = String(filename || '').trim();
      if (!wanted) return null;
      return (
        evidenceAttachments.find((attachment: any) => String(attachment?.filename || '').trim() === wanted) || null
      );
    },
    [evidenceAttachments],
  );
  const snapshotAttachment = _bestSnapshotAttachment(selectedIncident);
  const {
    strictReviewerImagePolicy,
    reviewerAidSnapshotRef,
    sourceTruthSnapshotRef,
    sourceTruthClipRef,
    primaryReviewerImageLabel,
    secondaryEvidenceLabel,
    reviewerAidBboxStatusLabel,
    incidentSubjectLabel,
    incidentSubjectMetadataChipLabel,
    incidentSubjectConfidence,
    forkliftSubjectMetadataChipLabel,
    forkliftConfidence,
    triggerVerificationLabel,
    triggerVerificationStatus,
  } = React.useMemo(
    () => buildReviewerImageDrawerContext(selectedIncident),
    [selectedIncident],
  );
  const latestVideoAttachment = _bestVideoAttachment ? _bestVideoAttachment(selectedIncident) : (() => {
    const latestVideo = evidenceLatest?.video;
    if (latestVideo?.filename) return latestVideo;
    const videoAttachments = evidenceAttachments.filter((attachment: any) => {
      const filename = String(attachment?.filename || '').trim();
      return Boolean(filename) && /\.(mp4|mov|mkv|webm)$/i.test(filename);
    });
    if (!videoAttachments.length) return null;
    return videoAttachments
      .slice()
      .sort((a: any, b: any) => {
        const aTs = a?.uploaded_at ? Date.parse(String(a.uploaded_at)) : 0;
        const bTs = b?.uploaded_at ? Date.parse(String(b.uploaded_at)) : 0;
        return bTs - aTs;
      })[0];
  })();
  const snapshotAttachmentMeta = React.useMemo(
    () =>
      evidenceAttachmentByFilename(snapshotAttachment?.filename) ||
      evidenceLatest?.snapshot ||
      (selectedIncident as any)?.review_media?.source_truth?.snapshot ||
      null,
    [evidenceAttachmentByFilename, evidenceLatest?.snapshot, selectedIncident, snapshotAttachment?.filename],
  );
  const clipAttachmentMeta = React.useMemo(
    () =>
      evidenceAttachmentByFilename(latestVideoAttachment?.filename) ||
      evidenceLatest?.video ||
      (selectedIncident as any)?.review_media?.source_truth?.clip ||
      null,
    [evidenceAttachmentByFilename, evidenceLatest?.video, latestVideoAttachment?.filename, selectedIncident],
  );
  const eventStartedTimeMs = React.useMemo(
    () =>
      parseTimingMs(snapshotAttachmentMeta?.metadata?.condition_started_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.condition_started_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.event_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.event_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.condition_started_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.event_started_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.condition_started_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.event_started_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.event_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.event_ts) ||
      parseTimingMs(selectedIncident?.created_at),
    [clipAttachmentMeta, parseTimingMs, selectedIncident, snapshotAttachmentMeta],
  );
  const rawAlertTriggeredTimeMs = React.useMemo(
    () =>
      parseTimingMs(snapshotAttachmentMeta?.metadata?.threshold_crossed_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.threshold_crossed_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.alert_triggered_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.alert_triggered_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.threshold_crossed_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.alert_triggered_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.threshold_crossed_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.alert_triggered_ts_ms) ||
      null,
    [clipAttachmentMeta, parseTimingMs, selectedIncident, snapshotAttachmentMeta],
  );
  const captureRequestedTimeMs = React.useMemo(
    () =>
      parseTimingMs(snapshotAttachmentMeta?.metadata?.capture_requested_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.capture_requested_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.capture_requested_at) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.capture_requested_at) ||
      parseTimingMs(
        Number.isFinite(Number(snapshotAttachmentMeta?.metadata?.capture_requested_epoch))
          ? Number(snapshotAttachmentMeta?.metadata?.capture_requested_epoch) * 1000
          : null,
      ) ||
      parseTimingMs(
        Number.isFinite(Number(clipAttachmentMeta?.metadata?.capture_requested_epoch))
          ? Number(clipAttachmentMeta?.metadata?.capture_requested_epoch) * 1000
          : null,
      ) ||
      parseTimingMs((selectedIncident as any)?.timing?.capture_requested_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.capture_requested_at) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.capture_requested_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.capture_requested_at) ||
      null,
    [clipAttachmentMeta, parseTimingMs, selectedIncident, snapshotAttachmentMeta],
  );
  const inferredAlertTriggeredTimeMs = React.useMemo(() => {
    if (!eventStartedTimeMs) return null;
    const thresholdSecondsRaw =
      (selectedIncident as any)?.metrics?.attributes?.threshold_seconds ??
      (selectedIncident as any)?.metrics?.threshold_seconds ??
      (selectedIncident as any)?.raw?.metrics?.attributes?.threshold_seconds;
    const persistenceSecondsRaw =
      (selectedIncident as any)?.metrics?.attributes?.persistence_seconds ??
      (selectedIncident as any)?.metrics?.persistence_seconds ??
      (selectedIncident as any)?.raw?.metrics?.attributes?.persistence_seconds;
    const thresholdSeconds = Number(thresholdSecondsRaw);
    const persistenceSeconds = Number(persistenceSecondsRaw);
    const totalSeconds =
      (Number.isFinite(thresholdSeconds) && thresholdSeconds > 0 ? thresholdSeconds : 0) +
      (Number.isFinite(persistenceSeconds) && persistenceSeconds > 0 ? persistenceSeconds : 0);
    if (totalSeconds <= 0) return null;
    const candidate = eventStartedTimeMs + Math.round(totalSeconds * 1000);
    const upperBound = captureRequestedTimeMs || rawAlertTriggeredTimeMs || null;
    if (upperBound && candidate > upperBound) return upperBound;
    return candidate;
  }, [captureRequestedTimeMs, eventStartedTimeMs, rawAlertTriggeredTimeMs, selectedIncident]);
  const alertTriggeredTimeMs = React.useMemo(() => {
    if (
      rawAlertTriggeredTimeMs &&
      captureRequestedTimeMs &&
      Math.abs(rawAlertTriggeredTimeMs - captureRequestedTimeMs) <= 1500 &&
      inferredAlertTriggeredTimeMs &&
      inferredAlertTriggeredTimeMs < rawAlertTriggeredTimeMs - 1500
    ) {
      return inferredAlertTriggeredTimeMs;
    }
    return rawAlertTriggeredTimeMs || inferredAlertTriggeredTimeMs;
  }, [captureRequestedTimeMs, inferredAlertTriggeredTimeMs, rawAlertTriggeredTimeMs]);
  const evidenceTargetTimeMs = React.useMemo(
    () =>
      parseTimingMs(snapshotAttachmentMeta?.metadata?.evidence_frame_target_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.evidence_frame_target_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.evidence_frame_target_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.evidence_frame_target_ts_ms) ||
      alertTriggeredTimeMs ||
      captureRequestedTimeMs ||
      eventStartedTimeMs,
    [alertTriggeredTimeMs, captureRequestedTimeMs, clipAttachmentMeta, eventStartedTimeMs, parseTimingMs, selectedIncident, snapshotAttachmentMeta],
  );
  const evidenceAnchorTimeMs = React.useMemo(
    () =>
      parseTimingMs((selectedIncident as any)?.timing?.capture_requested_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.timing?.evidence_anchor_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.capture_requested_ts_ms) ||
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.evidence_anchor_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.capture_requested_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.capture_requested_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.evidence_anchor_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.evidence_anchor_ts_ms) ||
      captureRequestedTimeMs ||
      parseTimingMs(clipAttachmentMeta?.metadata?.extra?.clip_start_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.evidence_ts_ms) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.evidence_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.captured_at) ||
      parseTimingMs(clipAttachmentMeta?.metadata?.captured_at) ||
      alertTriggeredTimeMs ||
      eventStartedTimeMs,
    [alertTriggeredTimeMs, captureRequestedTimeMs, clipAttachmentMeta, eventStartedTimeMs, parseTimingMs, selectedIncident, snapshotAttachmentMeta],
  );
  const snapshotFrameTimeMs = React.useMemo(
    () =>
      parseTimingMs(snapshotAttachmentMeta?.metadata?.evidence_ts_ms) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.timestamp) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.captured_at) ||
      parseTimingMs(snapshotAttachmentMeta?.captured_at) ||
      parseEvidenceFilenameMs(snapshotAttachmentMeta?.filename) ||
      null,
    [parseEvidenceFilenameMs, parseTimingMs, snapshotAttachmentMeta],
  );
  const snapshotCapturedAtMs = React.useMemo(
    () =>
      parseTimingMs(snapshotAttachmentMeta?.metadata?.extra?.capture_completed_at) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.timestamp) ||
      parseTimingMs(snapshotAttachmentMeta?.metadata?.captured_at) ||
      parseEvidenceFilenameMs(snapshotAttachmentMeta?.filename) ||
      null,
    [parseEvidenceFilenameMs, parseTimingMs, snapshotAttachmentMeta],
  );
  const snapshotCloudReceivedAtMs = React.useMemo(
    () => parseTimingMs(snapshotAttachmentMeta?.uploaded_at) || null,
    [parseTimingMs, snapshotAttachmentMeta],
  );
  const snapshotDeltaMs = React.useMemo(() => {
    if (evidenceTargetTimeMs && snapshotFrameTimeMs) {
      return snapshotFrameTimeMs - evidenceTargetTimeMs;
    }
    const canonicalDelta =
      parseTimingMs((selectedIncident as any)?.timing?.evidence_delta_ms) ??
      parseTimingMs((selectedIncident as any)?.envelope_metadata?.timing?.evidence_delta_ms);
    if (canonicalDelta !== null) return canonicalDelta;
    if (snapshotAttachmentMeta?.metadata?.evidence_delta_ms !== undefined && snapshotAttachmentMeta?.metadata?.evidence_delta_ms !== null) {
      const raw = Number(snapshotAttachmentMeta.metadata.evidence_delta_ms);
      if (Number.isFinite(raw)) return raw;
    }
    return null;
  }, [evidenceTargetTimeMs, parseTimingMs, selectedIncident, snapshotAttachmentMeta, snapshotFrameTimeMs]);
  const clipTiming = React.useMemo(
    () =>
      computeIncidentClipTiming({
        clipAttachmentMeta,
        selectedIncident,
        evidenceTargetTimeMs,
        parseTimingMs,
        parseEvidenceFilenameMs,
      }),
    [clipAttachmentMeta, evidenceTargetTimeMs, parseEvidenceFilenameMs, parseTimingMs, selectedIncident],
  );
  const eventLeadMs = React.useMemo(() => {
    if (eventStartedTimeMs && alertTriggeredTimeMs) return alertTriggeredTimeMs - eventStartedTimeMs;
    if (eventStartedTimeMs && evidenceAnchorTimeMs) return evidenceAnchorTimeMs - eventStartedTimeMs;
    return null;
  }, [alertTriggeredTimeMs, eventStartedTimeMs, evidenceAnchorTimeMs]);
  const captureLeadMs = React.useMemo(() => {
    if (alertTriggeredTimeMs && evidenceAnchorTimeMs) return evidenceAnchorTimeMs - alertTriggeredTimeMs;
    return null;
  }, [alertTriggeredTimeMs, evidenceAnchorTimeMs]);
  const showSeparateAlertMoment = Boolean(
    alertTriggeredTimeMs &&
    evidenceAnchorTimeMs &&
    Math.abs(evidenceAnchorTimeMs - alertTriggeredTimeMs) > 1500,
  );
  const evidenceTimingAvailable = Boolean(eventStartedTimeMs || evidenceTargetTimeMs || evidenceAnchorTimeMs || snapshotFrameTimeMs || clipTiming.startMs || clipTiming.capturedAtMs);
  const snapshotLooksDelayed = snapshotDeltaMs !== null && Math.abs(snapshotDeltaMs) > 5000;
  const hasSnapshotArtifact = Boolean(snapshotAttachment?.filename || evidenceLatest?.snapshot?.filename);
  const hasClipArtifact = Boolean(latestVideoAttachment?.filename);
  const effectiveSnapshotStatus = (() => {
    const raw = String((selectedIncident as any)?.snapshot_status || '').toUpperCase();
    return raw === 'AVAILABLE' || Boolean((selectedIncident as any)?.snapshot_uri) || hasSnapshotArtifact
      ? 'AVAILABLE'
      : raw === 'FAILED'
        ? 'FAILED'
        : 'PENDING';
  })();
  const effectiveClipStatus = resolveHonestClipStatus(selectedIncident as any, { hasClipArtifact });
  const effectiveMetaStatus = (() => {
    const raw = String((selectedIncident as any)?.meta_status || '').toUpperCase();
    return raw === 'AVAILABLE' || (selectedIncident as any)?.meta_uri ? 'AVAILABLE' : raw || 'PENDING';
  })();
  const summaryExplanation = (() => {
    const note = selectedIncident ? incidentStateExplanatoryNote(selectedIncident) : '';
    if (note) return note;
    if (selectedIncident) {
      return incidentDisplaySummaryForRow(selectedIncident, {
        zoneName: selectedZoneName,
        cameraName: selectedCameraName,
      });
    }
    if (selectedIncident) {
      return 'Keep ownership clear and move this incident through review and follow-through.';
    }
    return 'Load an incident to review it.';
  })();

  const proofSummary = (() => {
    if (!selectedIncident) return '';
    const attachments = evidenceAttachments.length;
    if (effectiveSnapshotStatus === 'AVAILABLE' && effectiveClipStatus === 'AVAILABLE') return 'snapshot and clip are available';
    if (effectiveSnapshotStatus === 'AVAILABLE') return 'snapshot is available';
    if (effectiveClipStatus === 'AVAILABLE') return 'clip is available';
    if (attachments > 0) return `${attachments} evidence file${attachments === 1 ? '' : 's'} ready`;
    if (effectiveSnapshotStatus === 'PENDING' || effectiveClipStatus === 'PENDING') return 'proof is still being prepared';
    return 'proof is still limited';
  })();

  const followUpSignal = Boolean(
    String((selectedGroupContext as any)?.recommended_action || '').trim() ||
    (Array.isArray((selectedIncident as any)?.taxonomy?.recommended_actions) &&
      (selectedIncident as any).taxonomy.recommended_actions.length > 0) ||
    ['acknowledged', 'in_progress'].includes(String((selectedIncident as any)?.ops_state || '').toLowerCase()),
  );

  const oneLineSummary = (() => {
    if (selectedGroupContext) {
      return incidentDisplaySummaryForRow(selectedGroupContext as any, {
        zoneName: selectedGroupZoneName,
        cameraName: selectedGroupCameraName,
      });
    }
    if (selectedIncident) {
      return incidentDisplaySummaryForRow(selectedIncident, {
        zoneName: selectedZoneName,
        cameraName: selectedCameraName,
      });
    }
    return 'Select an incident from the queue.';
  })();

  const policyContext = React.useMemo(() => {
    const base = selectedIncident
      ? incidentPolicyContextForRow(selectedIncident, { zoneName: selectedZoneName, cameraName: selectedCameraName })
      : selectedGroupContext
        ? incidentPolicyContextForRow(selectedGroupContext as any, { zoneName: selectedGroupZoneName, cameraName: selectedGroupCameraName })
        : [];
    const next = [...base];
    const setItem = (label: string, value: string) => {
      if (!value) return;
      const idx = next.findIndex((item) => String(item.label || '').trim().toLowerCase() === label.trim().toLowerCase());
      if (idx >= 0) {
        next[idx] = { ...next[idx], value };
        return;
      }
      next.unshift({ label, value });
    };
    setItem('Detection rule', buyerDetectionRuleLabel);
    setItem('Scan meaning', buyerScanMeaning);
    setItem('Scan template', String(matchedPolicyContext?.scan_template || '').trim());
    setItem('Exposure category', String(matchedPolicyContext?.exposure_category || '').trim());
    setItem(
      'Claims alignment category',
      String(matchedPolicyContext?.claims_roi_alignment?.claims_alignment_category || '').trim(),
    );
    setItem(
      'Commercial priority',
      String(matchedPolicyContext?.claims_roi_alignment?.commercial_priority || '').trim(),
    );
    return next;
  }, [
    buyerDetectionRuleLabel,
    buyerScanMeaning,
    matchedPolicyContext,
    selectedCameraName,
    selectedGroupCameraName,
    selectedGroupContext,
    selectedGroupZoneName,
    selectedIncident,
    selectedZoneName,
  ]);
  const policyContextValue = (label: string) =>
    policyContext.find((item) => String(item.label || '').trim().toLowerCase() === label.trim().toLowerCase())?.value || '';

  const selectedGroupKey = safeText((selectedGroupContext as any)?.group_key);
  const reviewSurfaceMode = reviewSurfaceModeFromParent ?? resolveIncidentReviewSurfaceMode({
    reviewExactMode,
    selectedIncident,
    selectedGroupContext: (selectedGroupContext as IncidentGroup | null) || null,
  });
  const observationDispositionMode = isObservationDispositionMode(reviewSurfaceMode);
  const groupedReviewMode = isPatternCaseReviewMode(reviewSurfaceMode);
  const contextChips = observationDispositionMode
    ? (selectedIncident
      ? incidentInsightChipsForRow(selectedIncident, {
          currentUser: String(user?.username || ''),
          includeOwnership: true,
          includeEvidence: true,
          includeRelatedAlerts: false,
        })
      : [])
    : selectedGroupContext
      ? incidentInsightChipsForRow(selectedGroupContext as any, { includeOwnership: false, includeEvidence: true })
      : selectedIncident
        ? incidentInsightChipsForRow(selectedIncident, {
            currentUser: String(user?.username || ''),
            includeOwnership: true,
            includeEvidence: true,
          })
        : [];
  const journeyGroupKey = groupedReviewMode ? selectedGroupKey : '';
  const journeyInitialStep: JourneyStepId = observationDispositionMode
    ? 'step2'
    : journeyGroupKey
      ? 'step0'
      : 'step1';
  const drawerStep0Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep1Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep2Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep3Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep4Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep5Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep6Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep7Ref = React.useRef<HTMLDivElement | null>(null);
  const drawerStep8Ref = React.useRef<HTMLDivElement | null>(null);
  const [activeJourneyStep, setActiveJourneyStep] = React.useState<JourneyStepId>(journeyInitialStep);
  const [completedJourneySteps, setCompletedJourneySteps] = React.useState<Partial<Record<JourneyStepId, boolean>>>({});
  const [ruleHelpAnchorEl, setRuleHelpAnchorEl] = React.useState<HTMLElement | null>(null);
  const [notificationEditorOpen, setNotificationEditorOpen] = React.useState(false);
  const [notificationEditorBusy, setNotificationEditorBusy] = React.useState(false);
  const [notificationEditorError, setNotificationEditorError] = React.useState<string | null>(null);
  const [notificationEditorDeployNow, setNotificationEditorDeployNow] = React.useState(true);
  const [groupedDeepReviewOpen, setGroupedDeepReviewOpen] = React.useState(false);
  const [emailTargetDraft, setEmailTargetDraft] = React.useState('');
  const [smsTargetDraft, setSmsTargetDraft] = React.useState('');
  const [webhookTargetDraft, setWebhookTargetDraft] = React.useState('');
  const [heatmapLoading, setHeatmapLoading] = React.useState(false);
  const [heatmapError, setHeatmapError] = React.useState<string | null>(null);
  const [heatmapData, setHeatmapData] = React.useState<IncidentGroupHeatmapResponse | null>(null);
  const [heatmapWindowDays, setHeatmapWindowDays] = React.useState<1 | 7 | 30>(7);
  const heatmapCacheRef = React.useRef<Map<string, IncidentGroupHeatmapResponse>>(new Map());
  const [edgeOutboxSummaryLoading, setEdgeOutboxSummaryLoading] = React.useState(false);
  const [edgeOutboxSummaryError, setEdgeOutboxSummaryError] = React.useState<string | null>(null);
  const [edgeOutboxSummary, setEdgeOutboxSummary] = React.useState<Record<string, any> | null>(null);
  const [edgeIncidentOutbox, setEdgeIncidentOutbox] = React.useState<Record<string, any> | null>(null);
  const journeyProgressStorageKey = React.useMemo(() => {
    const incidentId = safeText(selectedIncident?.incident_id);
    if (journeyGroupKey) return `${JOURNEY_PROGRESS_STORAGE_PREFIX}:group:${journeyGroupKey}`;
    if (incidentId) return `${JOURNEY_PROGRESS_STORAGE_PREFIX}:incident:${incidentId}`;
    return '';
  }, [journeyGroupKey, selectedIncident?.incident_id]);
  const journeyEvidenceFingerprint = React.useMemo(
    () => buildJourneyEvidenceFingerprint(selectedIncident),
    [selectedIncident],
  );
  const reviewJourneySessionId = React.useMemo(
    () => createReviewSessionId(),
    [journeyProgressStorageKey, renderMode],
  );

  const journeyStepRefs: Record<JourneyStepId, React.RefObject<HTMLDivElement>> = {
    step0: drawerStep0Ref,
    step1: drawerStep1Ref,
    step2: drawerStep2Ref,
    step3: drawerStep3Ref,
    step4: drawerStep4Ref,
    step5: drawerStep5Ref,
    step6: drawerStep6Ref,
    step7: drawerStep7Ref,
    step8: drawerStep8Ref,
  };

  React.useEffect(() => {
    setGroupedDeepReviewOpen(!groupedReviewMode);
  }, [groupedReviewMode, selectedIncident?.incident_id, journeyGroupKey, viewDialogOpen]);

  const journeySteps = React.useMemo(
    () => {
      if (observationDispositionMode) {
        return [
          { id: 'step2' as const, label: '1', title: 'Evidence' },
          { id: 'step5' as const, label: '2', title: 'Disposition' },
        ];
      }
      return [
        ...(selectedGroupKey && groupedReviewMode ? [{ id: 'step0' as const, label: '0', title: 'Risk pattern' }] : []),
        { id: 'step1' as const, label: '1', title: 'Confirm evidence' },
        { id: 'step2' as const, label: '2', title: 'Verify evidence' },
        { id: 'step3' as const, label: '3', title: 'Verify trigger' },
        { id: 'step4' as const, label: '4', title: 'Verify current condition' },
        { id: 'step5' as const, label: '5', title: 'Choose next action' },
        { id: 'step6' as const, label: '6', title: 'Plan follow-up' },
        { id: 'step7' as const, label: '7', title: 'Assign follow-up owner' },
        { id: 'step8' as const, label: '8', title: 'Confirm review record' },
      ];
    },
    [groupedReviewMode, observationDispositionMode, selectedGroupKey],
  );

  const getCurrentScrollTop = React.useCallback(() => {
    if (isPageMode) {
      if (typeof window === 'undefined') return 0;
      return window.scrollY || document.documentElement.scrollTop || 0;
    }
    return drawerScrollRef.current?.scrollTop ?? 0;
  }, [isPageMode]);
  const getJourneyStepTop = React.useCallback((stepId: JourneyStepId) => {
    const target = journeyStepRefs[stepId].current;
    if (!target) return null;
    if (isPageMode) {
      if (typeof window === 'undefined') return null;
      return target.getBoundingClientRect().top + (window.scrollY || document.documentElement.scrollTop || 0);
    }
    return target.offsetTop ?? null;
  }, [isPageMode]);

  const scrollToJourneyStep = React.useCallback(
    (stepId: JourneyStepId) => {
      const target = journeyStepRefs[stepId].current;
      if (!target) return;
      const railHeight = journeyRailRef.current?.getBoundingClientRect().height ?? 0;
      if (isPageMode) {
        if (typeof window === 'undefined') return;
        const absoluteTop = target.getBoundingClientRect().top + (window.scrollY || document.documentElement.scrollTop || 0);
        const nextTop = Math.max(0, absoluteTop - railHeight - 18);
        safeWindowScrollTo(nextTop, 'smooth');
      } else {
        const scrollRegion = drawerScrollRef.current;
        if (!scrollRegion) return;
        const targetRect = target.getBoundingClientRect();
        const scrollRect = scrollRegion.getBoundingClientRect();
        const relativeTop = targetRect.top - scrollRect.top + scrollRegion.scrollTop;
        const nextTop = Math.max(0, relativeTop - railHeight - 18);
        if (typeof scrollRegion.scrollTo === 'function') scrollRegion.scrollTo({ top: nextTop, behavior: 'smooth' });
        else scrollRegion.scrollTop = nextTop;
      }
      setActiveJourneyStep(stepId);
    },
    [isPageMode, safeWindowScrollTo],
  );

  React.useEffect(() => {
    setActiveJourneyStep(journeyInitialStep);
    if (groupedReviewMode) {
      setCompletedJourneySteps({});
      return;
    }
    if (!journeyProgressStorageKey) {
      setCompletedJourneySteps({});
      return;
    }
    try {
      const raw = localStorage.getItem(journeyProgressStorageKey);
      if (!raw) {
        setCompletedJourneySteps({});
        return;
      }
      const parsed = JSON.parse(raw) as JourneyProgressSnapshot | Record<string, boolean>;
      const isLegacyStepMap = !Array.isArray((parsed as JourneyProgressSnapshot)?.completed_step_ids);
      const completedStepIds = Array.isArray((parsed as JourneyProgressSnapshot)?.completed_step_ids)
        ? (parsed as JourneyProgressSnapshot).completed_step_ids || []
        : Object.entries(parsed || {})
            .filter(([, completed]) => Boolean(completed))
            .map(([stepId]) => stepId);
      const next: Partial<Record<JourneyStepId, boolean>> = {};
      journeySteps.forEach((step) => {
        if (completedStepIds.includes(step.id)) next[step.id] = true;
      });
      const storedEvidenceFingerprint = normalizeJourneyFingerprintText(
        (parsed as JourneyProgressSnapshot)?.evidence_fingerprint,
      );
      const evidenceChanged = Boolean(storedEvidenceFingerprint)
        ? storedEvidenceFingerprint !== journeyEvidenceFingerprint
        : isLegacyStepMap;
      if (evidenceChanged) {
        delete next[EVIDENCE_REVIEW_STEP_ID];
      }
      setCompletedJourneySteps(next);
      if (evidenceChanged) {
        setActiveJourneyStep(EVIDENCE_REVIEW_STEP_ID);
      }
    } catch {
      setCompletedJourneySteps({});
    }
  }, [
    groupedReviewMode,
    journeyEvidenceFingerprint,
    journeyProgressStorageKey,
    journeySteps,
    journeyInitialStep,
    selectedIncident?.incident_id,
    viewDialogOpen,
  ]);

  React.useEffect(() => {
    if (groupedReviewMode) return;
    if (!journeyProgressStorageKey) return;
    try {
      const completedStepIds: JourneyStepId[] = [];
      journeySteps.forEach((step) => {
        if (completedJourneySteps[step.id]) completedStepIds.push(step.id);
      });
      if (completedStepIds.length === 0) {
        localStorage.removeItem(journeyProgressStorageKey);
        return;
      }
      const next: JourneyProgressSnapshot = {
        completed_step_ids: completedStepIds,
        evidence_fingerprint: journeyEvidenceFingerprint,
      };
      localStorage.setItem(journeyProgressStorageKey, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
  }, [completedJourneySteps, groupedReviewMode, journeyEvidenceFingerprint, journeyProgressStorageKey, journeySteps]);

  const didInitialPageScrollRef = React.useRef(false);
  React.useEffect(() => {
    if (!viewDialogOpen) {
      didInitialPageScrollRef.current = false;
      return;
    }
    if (isPageMode) {
      // One-shot on first open so heatmap / representative swaps do not yank the user's scroll.
      if (didInitialPageScrollRef.current) return;
      didInitialPageScrollRef.current = true;
      if (typeof window === 'undefined') return;
      const shellRectTop = reviewShellRef.current?.getBoundingClientRect().top;
      const shellTop = (shellRectTop ?? 0) + (window.scrollY || document.documentElement.scrollTop || 0);
      safeWindowScrollTo(Math.max(0, shellTop - 12), 'auto');
      return;
    }
    if (!drawerScrollRef.current) return;
    drawerScrollRef.current.scrollTop = 0;
  }, [isPageMode, safeWindowScrollTo, viewDialogOpen, selectedIncident?.incident_id, journeyGroupKey]);

  React.useEffect(() => {
    const updateActiveStep = () => {
      const scrollTop = getCurrentScrollTop();
      const threshold = 112;
      let nextStep: JourneyStepId = journeyInitialStep;
      journeySteps.forEach((step) => {
        const stepId = step.id;
        const top = getJourneyStepTop(stepId);
        if (top != null && scrollTop + threshold >= top) nextStep = stepId;
      });
      setActiveJourneyStep((current) => (current === nextStep ? current : nextStep));
    };

    if (!viewDialogOpen) return;
    updateActiveStep();
    if (isPageMode) {
      window.addEventListener('scroll', updateActiveStep, { passive: true });
      return () => window.removeEventListener('scroll', updateActiveStep);
    }
    const scrollRegion = drawerScrollRef.current;
    if (!scrollRegion) return;
    scrollRegion.addEventListener('scroll', updateActiveStep, { passive: true });
    return () => scrollRegion.removeEventListener('scroll', updateActiveStep);
  }, [getCurrentScrollTop, getJourneyStepTop, isPageMode, journeyInitialStep, journeySteps, viewDialogOpen]);

  const affectedEntitySummary = (() => {
    if (!selectedIncident) return '';
    const metrics = (selectedIncident.metrics && typeof selectedIncident.metrics === 'object') ? selectedIncident.metrics as Record<string, any> : {};
    const attrs = (metrics.attributes && typeof metrics.attributes === 'object') ? metrics.attributes as Record<string, any> : {};
    const actors = (selectedIncident.actors && typeof selectedIncident.actors === 'object') ? selectedIncident.actors as Record<string, any> : {};
    const subjectRaw =
      safeText(attrs.subject) ||
      safeText(metrics.subject) ||
      safeText(attrs.actor_type) ||
      safeText(metrics.actor_type) ||
      safeText(actors.subject) ||
      safeText(actors.class_label);
    const incidentType = safeText(selectedIncident.incident_type).toLowerCase();
    const subject =
      subjectRaw
        ? titleCaseWords(subjectRaw)
        : incidentType.includes('forklift')
          ? 'Forklift'
          : 'Person';
    const identity =
      safeText(actors.global_track_id) ||
      safeText(actors.person_track_id) ||
      safeText(actors.pedestrian_track_id) ||
      safeText(actors.forklift_track_id) ||
      safeText(actors.track_id) ||
      safeText(attrs.subject_key) ||
      safeText(attrs.track_id) ||
      safeText(metrics.track_id) ||
      safeText(attrs.object_id);
    return identity ? `${subject} (${identity})` : subject;
  })();

  const attentionReasonSummary = (() => {
    if (!selectedIncident) return '';
    const failedEvidence: string[] = [];
    if (String(selectedIncident.snapshot_status || '').toUpperCase() === 'FAILED') failedEvidence.push('snapshot capture failed');
    if (String(selectedIncident.clip_status || '').toUpperCase() === 'FAILED') failedEvidence.push('clip capture failed');
    if (String(selectedIncident.meta_status || '').toUpperCase() === 'FAILED') failedEvidence.push('context export failed');
    if (failedEvidence.length > 0) {
      return `Evidence collection is incomplete because ${failedEvidence.join(', ')}.`;
    }
    if (safeText(selectedIncident.degraded_reason)) {
      return `Alert delivery is degraded: ${safeText(selectedIncident.degraded_reason)}.`;
    }
    if (String(selectedIncident.edge_state || '').trim().toLowerCase() === 'active') {
      return 'The edge still reports this unsafe condition as active, so the incident remains open for operator review.';
    }
    if (String(selectedIncident.edge_state || '').trim().toLowerCase() === 'cleared') {
      return safeText(selectedIncident.edge_clear_reason)
        ? `The edge reports the condition cleared (${safeText(selectedIncident.edge_clear_reason)}), but workflow follow-up is still open.`
        : 'The edge reports the condition cleared, but workflow follow-up is still open.';
    }
    const status = String(selectedIncident.status || '').trim().toUpperCase();
    if (status === 'OPEN') return 'No operator owns this incident yet. Acknowledge it to establish ownership before deeper review.';
    if (status === 'ACKED') return 'Ownership is set, but review has not started yet.';
    if (status === 'IN_REVIEW') return 'Review is still in progress or follow-up work has not been completed yet.';
    if (status === 'SUPPRESSED') return 'Similar observations are currently suppressed, so review is focused on existing evidence and prior actions.';
    if (status === 'CLOSED') return 'Operator workflow is already complete for this incident.';
    return summaryExplanation;
  })();

  const riskPatternLabel = safeText(
    getIncidentGroupPatternLabel((selectedGroupContext as any) || (selectedIncident as any)?.raw || null)
      || (selectedGroupContext as any)?.risk_pattern?.label
      || (selectedIncident as any)?.raw?.risk_pattern?.label,
  );
  const riskPatternSummary = safeText(
    getIncidentGroupPatternSummary((selectedGroupContext as any) || (selectedIncident as any)?.raw || null)
      || (selectedGroupContext as any)?.risk_pattern?.summary
      || (selectedIncident as any)?.raw?.risk_pattern?.summary,
  );

  const targetSummary = (() => {
    if (!selectedIncident) return '';
    const zone = presentBuyerZoneLabel(selectedZoneName || safeText(selectedIncident.zone_id));
    const camera = selectedCameraName || safeText(selectedIncident.camera_id);
    const device = safeText(selectedIncident.device_id);
    if (_isAdvancedIncidentsUi) {
      return [zone, camera ? `Camera stream ${camera}` : '', device ? `Edge device ${device}` : ''].filter(Boolean).join(' · ');
    }
    return [zone, camera].filter(Boolean).join(' · ');
  })();

  const detectionFindingSummary = (() => {
    if (!selectedIncident) return '';
    const finding = _findingForIncident?.(selectedIncident);
    const explicit = safeText((finding as any)?.label);
    if (explicit) return explicit;
    const recommended = safeText(selectedIncident?.taxonomy?.description);
    if (recommended) return recommended;
    return oneLineSummary;
  })();
  const detectionRuleSummary =
    buyerDetectionRuleLabel ||
    policyContextValue('Detection rule') ||
    policyContextValue('Policy') ||
    safeText(selectedIncident?.policy_name) ||
    safeText(selectedIncident?.policy_id);
  const areaBehaviorSummary = policyContextValue('Area behavior');
  const sensitivitySummary = policyContextValue('Sensitivity');
  const edgeLiveSummary = selectedIncident ? formatEdgeLiveConditionLabel(selectedIncident.edge_state) : 'Not reported';
  const degradationSummary = safeText(selectedIncident?.degraded_reason);

  const confidenceSummary = (() => {
    if (!selectedIncident) return '';
    const raw =
      safeText((selectedIncident as any)?.confidence_indicator) ||
      safeText((selectedIncident.metrics as any)?.confidence_indicator) ||
      safeText((selectedIncident.metrics as any)?.attributes?.confidence_indicator);
    if (!raw) return 'Confidence not reported';
    if (raw.toLowerCase() === 'high') return 'High confidence';
    if (raw.toLowerCase() === 'estimated') return 'Estimated confidence';
    return titleCaseWords(raw);
  })();
  const forkliftConfidenceLabel = (() => {
    const attachmentMeta =
      snapshotAttachmentMeta?.metadata && typeof snapshotAttachmentMeta.metadata === 'object'
        ? (snapshotAttachmentMeta.metadata as Record<string, unknown>)
        : null;
    const numeric =
      extractIncidentForkliftConfidence(selectedIncident, attachmentMeta) ?? forkliftConfidence;
    return formatForkliftConfidenceLabel(numeric);
  })();
  const snapshotConfidenceLabel = (() => {
    const attachmentMeta =
      snapshotAttachmentMeta?.metadata && typeof snapshotAttachmentMeta.metadata === 'object'
        ? (snapshotAttachmentMeta.metadata as Record<string, unknown>)
        : null;
    const displayedRef = reviewerAidSnapshotRef || sourceTruthSnapshotRef;
    const numeric = extractReviewMediaConfidence(displayedRef, attachmentMeta);
    const formatted = forkliftConfidenceLabel
      ? formatPersonConfidenceLabel(numeric)
      : formatSnapshotConfidenceLabel(numeric);
    if (formatted) return formatted;
    return confidenceSummary && confidenceSummary !== 'Confidence not reported'
      ? confidenceSummary
      : '';
  })();
  const forkliftSubjectChipLabel =
    forkliftSubjectMetadataChipLabel ||
    resolveForkliftSubjectMetadataChipLabel(selectedIncident) ||
    '';

  const recurringIssueSummary = (() => {
    const recurrenceGroup = (selectedGroupContext as any) || (selectedIncident as any)?.raw || null;
    const recurrence = safeText(
      getIncidentGroupPatternLabel(recurrenceGroup)
      || (selectedGroupContext as any)?.recurrence_tag
      || (selectedIncident as any)?.raw?.recurrence_tag,
    );
    const riskLabel = riskPatternLabel;
    const riskSummary = riskPatternSummary;
    if (riskLabel && riskSummary) return `${riskLabel} · ${riskSummary}`;
    if (riskLabel) return riskLabel;
    if (recurrence && isIncidentGroupRecurring(recurrenceGroup)) return recurrence;
    if (recurrence) return recurrence;
    return 'No longer-term recurrence trend reported';
  })();
  const detectionSystemIssueSummary = (() => {
    const failedEvidence: string[] = [];
    if (String(selectedIncident?.snapshot_status || '').toUpperCase() === 'FAILED') failedEvidence.push('snapshot capture failed');
    if (String(selectedIncident?.clip_status || '').toUpperCase() === 'FAILED') failedEvidence.push('clip capture failed');
    if (String(selectedIncident?.meta_status || '').toUpperCase() === 'FAILED') failedEvidence.push('context export failed');
    if (failedEvidence.length > 0) return failedEvidence.join(', ');
    if (degradationSummary) return degradationSummary;
    return 'No detection-system issue reported';
  })();

  const reviewFocusSummary = (() => {
    if (!selectedIncident && !selectedGroupContext) return '';
    const taxonomyActions = Array.isArray((selectedIncident as any)?.taxonomy?.recommended_actions)
      ? (selectedIncident as any).taxonomy.recommended_actions
      : [];
    const investigationSteps = Array.isArray((selectedIncident as any)?.taxonomy?.investigation_steps)
      ? (selectedIncident as any).taxonomy.investigation_steps
      : [];
    const explicit =
      safeText((selectedGroupContext as any)?.recommended_action) ||
      safeText(taxonomyActions[0]) ||
      safeText(investigationSteps[0]);
    if (explicit) return explicit;
    return 'Confirm the evidence first, then decide whether follow-up remains open or this review can be closed.';
  })();

  const openDiagnostics = () => {
    const deviceId = String((selectedIncident as any)?.device_id || '').trim();
    if (!deviceId) return;
    navigate(`/diagnostics?device=${encodeURIComponent(deviceId)}`);
  };
  const openAuditTrail = () => {
    if (!selectedIncident?.incident_id) return;
    navigate(`/audit?resource_type=incident&resource_id=${encodeURIComponent(selectedIncident.incident_id)}`);
  };
  const openRiskAnalytics = () => {
    const params = new URLSearchParams();
    if (scopedSiteId) params.set('site', String(scopedSiteId));
    const zoneId = safeText((selectedIncident as any)?.zone_id || (selectedGroupContext as any)?.zone_id);
    if (zoneId) params.set('zone_id', zoneId);
    navigate(`/analytics${params.toString() ? `?${params.toString()}` : ''}`);
  };
  const incidentCreatedAtMs = React.useMemo(
    () => parseTimingMs(selectedIncident?.created_at) || null,
    [parseTimingMs, selectedIncident?.created_at],
  );
  const incidentCreateLagMs = React.useMemo(() => {
    if (!incidentCreatedAtMs || !eventStartedTimeMs) return null;
    return incidentCreatedAtMs - eventStartedTimeMs;
  }, [eventStartedTimeMs, incidentCreatedAtMs]);
  const incidentOutboxStats = React.useMemo(() => {
    const value = edgeOutboxSummary?.outbox_incident_create;
    return value && typeof value === 'object' ? value : null;
  }, [edgeOutboxSummary]);
  const evidenceOutboxStats = React.useMemo(() => {
    const value = edgeOutboxSummary?.outbox_evidence_upload;
    return value && typeof value === 'object' ? value : null;
  }, [edgeOutboxSummary]);
  const incidentOutboxQueuedCount = React.useMemo(() => {
    const counts = edgeIncidentOutbox?.counts_by_status;
    if (!counts || typeof counts !== 'object') return 0;
    return ['PENDING', 'RETRY_WAIT', 'LEASED', 'SENDING'].reduce((total, key) => {
      const next = Number((counts as Record<string, any>)[key] || 0);
      return total + (Number.isFinite(next) ? next : 0);
    }, 0);
  }, [edgeIncidentOutbox]);
  const incidentRetryWaitTop = React.useMemo(() => {
    const rows = edgeOutboxSummary?.errors?.retry_wait?.INCIDENT_CREATE;
    return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  }, [edgeOutboxSummary]);
  const evidenceRetryWaitTop = React.useMemo(() => {
    const rows = edgeOutboxSummary?.errors?.retry_wait?.EVIDENCE_UPLOAD;
    return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  }, [edgeOutboxSummary]);
  const edgeDeliveryState = React.useMemo(() => {
    if (!safeText(selectedIncident?.device_id)) {
      return {
        severity: 'info' as const,
        title: 'Edge delivery telemetry unavailable',
        detail: 'This incident is missing a device ID, so the edge outbox cannot be queried.',
      };
    }
    if (edgeOutboxSummaryLoading) {
      return {
        severity: 'info' as const,
        title: 'Checking edge delivery health',
        detail: 'Loading the edge outbox snapshot for this device.',
      };
    }
    if (edgeOutboxSummaryError) {
      return {
        severity: 'warning' as const,
        title: 'Edge delivery health unavailable',
        detail: edgeOutboxSummaryError,
      };
    }
    if (edgeOutboxSummary && edgeOutboxSummary.ok === false) {
      return {
        severity: 'warning' as const,
        title: 'Edge delivery health unavailable',
        detail: safeText(edgeOutboxSummary.detail || edgeOutboxSummary.error) || 'The edge outbox summary did not return successfully.',
      };
    }
    if (incidentOutboxQueuedCount > 0) {
      return {
        severity: 'warning' as const,
        title: groupedReviewMode
          ? 'The selected representative evidence sample is still queued on the edge'
          : 'This incident is still queued on the edge',
        detail: 'The incident create or follow-on delivery jobs are still pending or retrying on the device.',
      };
    }
    if ((incidentCreateLagMs !== null && incidentCreateLagMs > 60000) || Number(incidentOutboxStats?.oldest_pending_age_sec || 0) > 60) {
      return {
        severity: 'warning' as const,
        title: 'Edge backlog is draining',
        detail: 'This incident arrived later than it happened on the edge. Use edge timing for truth and treat cloud arrival as delivery lag.',
      };
    }
    if (Number(incidentOutboxStats?.active_total || 0) > 0 || Number(evidenceOutboxStats?.active_total || 0) > 0) {
      return {
        severity: 'info' as const,
        title: 'Edge queue is active',
        detail: 'Some create or evidence jobs are still draining, but this incident is no longer queued.',
      };
    }
    return {
      severity: 'success' as const,
      title: 'Edge delivery looks healthy',
      detail: 'Recent outbox signals do not show an active backlog for this device.',
    };
  }, [
    edgeOutboxSummary,
    edgeOutboxSummaryError,
    edgeOutboxSummaryLoading,
    evidenceOutboxStats?.active_total,
    groupedReviewMode,
    incidentCreateLagMs,
    incidentOutboxQueuedCount,
    incidentOutboxStats?.active_total,
    incidentOutboxStats?.oldest_pending_age_sec,
    selectedIncident?.device_id,
    safeText,
  ]);
  const nextReviewAtValue = safeText(opsSlaTargetDraft || selectedIncident?.sla_target_at);
  const nextReviewSummary = (() => {
    if (!nextReviewAtValue) return 'No next review time is set yet. Assign one below so the team knows when to check whether conditions improved.';
    const parsed = Date.parse(nextReviewAtValue);
    if (!Number.isFinite(parsed)) return nextReviewAtValue;
    return new Date(parsed).toLocaleString();
  })();
  const nextReviewRelativeSummary = (() => {
    if (!nextReviewAtValue) return 'Set a follow-up time after assigning ownership.';
    const parsed = Date.parse(nextReviewAtValue);
    if (!Number.isFinite(parsed)) return 'Follow-up review time recorded.';
    const deltaMs = parsed - Date.now();
    const absMinutes = Math.max(1, Math.round(Math.abs(deltaMs) / 60000));
    if (absMinutes < 60) return deltaMs >= 0 ? `Review again in about ${absMinutes} min.` : `Follow-up review was due about ${absMinutes} min ago.`;
    const absHours = Math.round(absMinutes / 60);
    if (absHours < 48) return deltaMs >= 0 ? `Review again in about ${absHours} hour${absHours === 1 ? '' : 's'}.` : `Follow-up review was due about ${absHours} hour${absHours === 1 ? '' : 's'} ago.`;
    const absDays = Math.round(absHours / 24);
    return deltaMs >= 0 ? `Review again in about ${absDays} day${absDays === 1 ? '' : 's'}.` : `Follow-up review was due about ${absDays} day${absDays === 1 ? '' : 's'} ago.`;
  })();
  const reviewWindowContext = React.useMemo(
    () => reviewWindowContextFromParent ?? resolveReviewWindowContext({
      timeRange: {
        fromDate: reviewTimeRange?.fromDate,
        toDate: reviewTimeRange?.toDate,
        label: String((reviewTimeRange as IncidentTimeRange | undefined)?.label || 'Last 24h'),
      },
      afterSignedScanReview,
    }),
    [
      afterSignedScanReview,
      reviewTimeRange?.fromDate,
      reviewTimeRange?.toDate,
      (reviewTimeRange as IncidentTimeRange | undefined)?.label,
      reviewWindowContextFromParent,
    ],
  );
  const groupedReviewModel = buildGroupedIncidentReviewModel({
    selectedIncident,
    selectedGroupContext: (selectedGroupContext as IncidentGroup | null) || null,
    reviewExactMode,
    selectedZoneName,
    selectedGroupZoneName,
    selectedCameraName,
    selectedGroupCameraName,
    canManageGroupPatterns,
    workflowLabel,
    opsStateLabel,
    proofSummary: incidentCanonicalEvidenceReadinessLabel(selectedIncident) || proofSummary,
    summaryExplanation,
    attentionReasonSummary,
    reviewFocusSummary,
    riskPatternSummary,
    riskPatternLabel,
    detectionRuleSummary,
    nextReviewSummary,
    safeText,
    presentAreaLabel,
    formatTimingInstant,
    parseTimingMs,
    titleCaseWords,
    reviewWindowContext,
    afterSignedScanReview,
    fullHistoryMode: reviewWindowContext.kind === 'full_case_history',
    timeRange: {
      fromDate: reviewTimeRange?.fromDate,
      toDate: reviewTimeRange?.toDate,
      label: String((reviewTimeRange as IncidentTimeRange | undefined)?.label || 'Last 24h'),
    },
  });
  const {
    pattern,
    actionScope: groupedActionScope,
    relatedOccurrenceCount: relatedIncidentCount,
    patternSummary: groupedSummaryText,
    patternRollupText: groupedIncidentExplanation,
    groupingBasisText: groupedReasonText,
    representativeIncidentId,
    representativeIncidentSummary,
    actionScopeSummary,
    groupedScopeSummary,
    groupedPolicyLabel,
    followUpOwnerSummary,
    workOrderSummary,
    patternActionAvailabilityMessage,
    groupedFirstSeenLabel,
    groupedLastSeenLabel,
    groupedEvidenceStatusLabel,
    groupedWhatToKnow,
    followUpStateSummary,
  } = groupedReviewModel;
  const patternVolumeCount = pattern.patternVolumeCount;
  const [patternDispositionRollup, setPatternDispositionRollup] = React.useState<IncidentReviewOutcomeRollupResponse | null>(null);
  React.useEffect(() => {
    if (!groupedReviewMode || !viewDialogOpen) {
      setPatternDispositionRollup(null);
      return;
    }
    const fromIso = String(reviewWindowContext?.fromIso || reviewTimeRange?.fromDate || '').trim();
    const toIso = String(reviewWindowContext?.toIso || reviewTimeRange?.toDate || '').trim();
    if (!fromIso || !toIso) {
      setPatternDispositionRollup(null);
      return;
    }
    const scope: any = selectedGroupContext || selectedIncident;
    const zoneId = String(scope?.zone_id || '').trim();
    const deviceId = String(scope?.device_id || '').trim();
    let cancelled = false;
    void (async () => {
      try {
        const rollup = await incidentsApi.getReviewOutcomeRollup({
          org_id: String(activeScope?.orgId || '').trim() || undefined,
          site_id: String(scopedSiteId || activeScope?.siteId || '').trim() || undefined,
          from_ts: fromIso,
          to_ts: toIso,
          zone_id: zoneId || undefined,
          device_id: deviceId || undefined,
        });
        if (!cancelled) setPatternDispositionRollup(rollup);
      } catch {
        if (!cancelled) setPatternDispositionRollup(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeScope?.orgId,
    activeScope?.siteId,
    groupedReviewMode,
    reviewTimeRange?.fromDate,
    reviewTimeRange?.toDate,
    reviewWindowContext?.fromIso,
    reviewWindowContext?.toIso,
    scopedSiteId,
    selectedGroupContext,
    selectedIncident,
    viewDialogOpen,
  ]);
  const patternDispositionSummary = React.useMemo(() => {
    if (!groupedReviewMode) return null;
    const scope: any = selectedGroupContext || selectedIncident;
    if (!scope) return null;
    const severity = String(scope?.severity || selectedIncident?.severity || '').trim();
    return buildPatternDispositionSummaryForPatternCase({
      patternScopeIncidents: patternScopeIncidents as Array<Record<string, unknown>>,
      rollup: patternDispositionRollup,
      totalObservations: patternVolumeCount > 0 ? patternVolumeCount : null,
      severity,
    });
  }, [
    groupedReviewMode,
    patternDispositionRollup,
    patternScopeIncidents,
    patternVolumeCount,
    selectedGroupContext,
    selectedIncident,
  ]);
  const afterSignedScanReviewActive = Boolean(afterSignedScanReview?.active);
  const riskPatternReviewPageState = riskPatternWorkflowReviewContext
    ? resolveRiskPatternReviewPageState(riskPatternWorkflowReviewContext)
    : null;
  const showAfterSignedScanReviewPanel = afterSignedScanReviewActive
    && afterSignedScanReview
    && riskPatternReviewPageState !== 'state_c';
  const effectiveReviewWindowDisplayCount = afterSignedScanReviewActive
    ? Math.max(0, Number(afterSignedScanScopedActivityCount ?? afterSignedScanReview?.newActivityCount ?? 0))
    : reviewWindowContext.kind === 'full_case_history'
      ? pattern.patternVolumeCount
      : pattern.reviewWindowDisplayCount;
  const reviewWindowDisplayCount = effectiveReviewWindowDisplayCount;
  const patternVolumeKind = pattern.patternVolumeKind;
  const hotspotOperationalMetrics = deriveGroupedHotspotOperationalMetrics({
    observationWindowCount: reviewWindowDisplayCount,
    group: groupedReviewMode ? selectedGroupContext as IncidentGroup | null : null,
    incidents: groupedReviewMode ? patternScopeIncidents : [],
  });
  const relatedEventSignalsSummary = reviewWindowDisplayCount > 1
    ? pattern.patternVolumeDisplay
    : 'No grouped pattern-observation surge reported';
  const reviewingLatestInSeries = Boolean(
    selectedIncident?.incident_id &&
    safeText((selectedGroupContext as any)?.latest_incident_id) &&
    safeText((selectedGroupContext as any)?.latest_incident_id) === safeText(selectedIncident?.incident_id),
  );
  const seriesContextSummary = (() => {
    if (observationDispositionMode) {
      return 'This review is focused on one observation. Use the parent pattern for representative evidence and follow-up.';
    }
    if (reviewWindowDisplayCount <= 1) return 'This review is focused on one pattern observation right now.';
    if (reviewingLatestInSeries) {
      return `This page is anchored on representative evidence from a recurring risk pattern with ${pattern.patternVolumeDisplay}.`;
    }
    if (selectedGroupContext) {
      return `This review item is part of a recurring risk pattern with ${pattern.patternVolumeDisplay}. Actions apply only to open rows in the selected review window.`;
    }
    return `This review item correlates with ${pattern.patternVolumeDisplay} in the current queue view.`;
  })();
  const seriesActionLabel = reviewWindowDisplayCount > 1
    ? 'View pattern observations'
    : '';
  const attachmentCount = Array.isArray(selectedIncident?.evidence?.attachments) ? selectedIncident.evidence.attachments.length : 0;
  const groupedProgressSummary = React.useMemo(() => {
    return deriveGroupedProgressSummary({
      workflowStatus: String(selectedIncident?.status || ''),
      opsState: String((selectedIncident as any)?.ops_state || ''),
      evidenceReady: effectiveSnapshotStatus === 'AVAILABLE' || effectiveClipStatus === 'AVAILABLE' || attachmentCount > 0,
      followUpStarted: Boolean(
        followUpSignal
        || safeText((selectedIncident as any)?.odoo_ticket_id)
        || safeText((selectedIncident as any)?.assignee)
        || safeText((selectedIncident as any)?.followup_owner)
        || nextReviewAtValue,
      ),
    });
  }, [
    attachmentCount,
    effectiveClipStatus,
    effectiveSnapshotStatus,
    followUpSignal,
    nextReviewAtValue,
    selectedIncident?.status,
    (selectedIncident as any)?.assignee,
    (selectedIncident as any)?.followup_owner,
    (selectedIncident as any)?.odoo_ticket_id,
    (selectedIncident as any)?.ops_state,
    safeText,
  ]);
  const closeoutHistoryState = (() => {
    if (incidentActivityLoading) return { label: 'History loading', color: 'info' as const };
    if (incidentActivityError) return { label: 'History unavailable', color: 'warning' as const };
    if ((incidentActivityItems?.length || 0) > 0) return { label: `${incidentActivityItems.length} history event${incidentActivityItems.length === 1 ? '' : 's'}`, color: 'success' as const };
    return { label: 'No history yet', color: 'default' as const };
  })();
  const closeoutCommentState = (() => {
    if (incidentCommentsLoading) return { label: 'Comments loading', color: 'info' as const };
    if (incidentCommentsError) return { label: 'Comments unavailable', color: 'warning' as const };
    if ((incidentComments?.length || 0) > 0) return { label: `${incidentComments.length} saved comment${incidentComments.length === 1 ? '' : 's'}`, color: 'success' as const };
    return { label: 'No saved comments', color: 'default' as const };
  })();
  const closeoutEvidenceState = attachmentCount > 0
    ? { label: `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'} on record`, color: 'success' as const }
    : { label: 'No extra attachments', color: 'default' as const };
  const workflowStatus = String(selectedIncident?.status || '').trim().toUpperCase();
  const followUpState = String(opsStateDraft || selectedIncident?.ops_state || '').trim().toLowerCase();
  const workflowIsClosed = workflowStatus === 'CLOSED' || workflowStatus === 'SUPPRESSED';
  const followUpMarkedComplete = followUpState === 'resolved' || followUpState === 'closed';
  const closeoutGuidance = workflowIsClosed
    ? {
        severity: 'success' as const,
        message: 'Incident workflow is closed. Notes, follow-up, and measurement stay here for audit and proof.',
      }
    : followUpMarkedComplete
      ? {
          severity: 'warning' as const,
          message: 'Follow-up is marked complete, but the incident workflow is still open. Use Close incident in Step 5 to finish the review.',
        }
      : {
          severity: 'info' as const,
          message: 'Review notes and follow-up can be complete while the incident is still open. Use Close incident in Step 5 when the review should end.',
        };
  const currentPolicyRecord = React.useMemo(() => {
    const policyId = safeText(selectedIncident?.policy_id || (selectedIncident as any)?.triggered_by?.policy_id);
    if (!policyId) return null;
    const policies = Array.isArray(allPolicies) ? allPolicies : [];
    return policies.find((policy: any) => String(policy?.policy_id || '').trim() === policyId) || null;
  }, [allPolicies, selectedIncident]);
  const policyScopeForEditing = React.useMemo(() => {
    const policyId = safeText(currentPolicyRecord?.policy_id || selectedIncident?.policy_id || (selectedIncident as any)?.triggered_by?.policy_id);
    const siteId = safeText(currentPolicyRecord?.site_id || selectedIncident?.site_id);
    const orgId = safeText(currentPolicyRecord?.org_id || selectedIncident?.org_id);
    return { policyId, siteId, orgId };
  }, [currentPolicyRecord, selectedIncident]);
  const notificationActionValue = React.useCallback((actionType: string, field: string): string => {
    const actions = Array.isArray(currentPolicyRecord?.actions) ? currentPolicyRecord.actions : [];
    const action = actions.find((value: any) => String(value?.type || '').trim() === actionType);
    return String(action?.params?.[field] ?? action?.[field] ?? '').trim();
  }, [currentPolicyRecord]);

  React.useEffect(() => {
    setEmailTargetDraft(notificationActionValue('email.notify', 'to'));
    setSmsTargetDraft(notificationActionValue('sms.notify', 'to'));
    setWebhookTargetDraft(notificationActionValue('webhook.call', 'url'));
    setNotificationEditorError(null);
  }, [notificationActionValue, policyScopeForEditing.policyId]);

  const heatmapQuery = React.useMemo(() => {
    if (!selectedIncident?.incident_id) return null;
    return {
      dimension: 'zone' as const,
      // Keep repeat timing independent from the case-review window; the user
      // explicitly chooses a rolling 1, 7, or 30-day basis.
      hours: 24 * heatmapWindowDays,
      from_date: undefined,
      to_date: undefined,
      incident_type: safeText(selectedIncident?.incident_type) || undefined,
      zone_id: safeText(selectedIncident?.zone_id) || undefined,
      camera_id: safeText(selectedIncident?.camera_id) || undefined,
      device_id: safeText(selectedIncident?.device_id) || undefined,
      limit_rows: 6,
    };
  }, [
    heatmapWindowDays,
    selectedIncident?.incident_id,
    selectedIncident?.incident_type,
    selectedIncident?.zone_id,
    selectedIncident?.camera_id,
    selectedIncident?.device_id,
  ]);

  const heatmapRequestKey = React.useMemo(
    () => (heatmapQuery ? JSON.stringify(heatmapQuery) : ''),
    [heatmapQuery],
  );

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!viewDialogOpen || !heatmapQuery) {
        if (!cancelled) {
          setHeatmapData(null);
          setHeatmapError(null);
          setHeatmapLoading(false);
        }
        return;
      }
      const cached = heatmapCacheRef.current.get(heatmapRequestKey);
      if (cached) {
        if (!cancelled) {
          setHeatmapData(cached);
          setHeatmapError(null);
          setHeatmapLoading(false);
        }
        if (typeof onHeatmapWindowChange === 'function') {
          onHeatmapWindowChange({
            fromDate: cached.from_ts || undefined,
            toDate: cached.to_ts || undefined,
          });
        }
        return;
      }
      try {
        setHeatmapLoading(true);
        setHeatmapError(null);
        const response = await incidentsApi.getIncidentGroupHeatmap(heatmapQuery);
        if (cancelled) return;
        heatmapCacheRef.current.set(heatmapRequestKey, response);
        setHeatmapData(response);
        if (typeof onHeatmapWindowChange === 'function') {
          onHeatmapWindowChange({
            fromDate: response.from_ts || undefined,
            toDate: response.to_ts || undefined,
          });
        }
      } catch (error: any) {
        if (cancelled) return;
        setHeatmapData(null);
        setHeatmapError(error?.response?.data?.error || error?.message || 'Failed to load recurring risk heatmap');
      } finally {
        if (!cancelled) setHeatmapLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    heatmapQuery,
    heatmapRequestKey,
    onHeatmapWindowChange,
    viewDialogOpen,
  ]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const deviceId = safeText(selectedIncident?.device_id);
      const incidentId = safeText(selectedIncident?.incident_id);
      if (!viewDialogOpen || !deviceId || !incidentId) {
        if (!cancelled) {
          setEdgeOutboxSummary(null);
          setEdgeIncidentOutbox(null);
          setEdgeOutboxSummaryError(null);
          setEdgeOutboxSummaryLoading(false);
        }
        return;
      }
      try {
        setEdgeOutboxSummaryLoading(true);
        setEdgeOutboxSummaryError(null);
        const [summary, incidentOutbox] = await Promise.all([
          incidentsApi.getEdgeOutboxSummary(deviceId),
          incidentsApi.getEdgeOutboxIncident(deviceId, incidentId, { limit: 80 }),
        ]);
        if (cancelled) return;
        setEdgeOutboxSummary(summary && typeof summary === 'object' ? summary : null);
        setEdgeIncidentOutbox(incidentOutbox && typeof incidentOutbox === 'object' ? incidentOutbox : null);
      } catch (error: any) {
        if (cancelled) return;
        setEdgeOutboxSummary(null);
        setEdgeIncidentOutbox(null);
        setEdgeOutboxSummaryError(
          error?.response?.data?.error || error?.message || 'Failed to load edge outbox telemetry',
        );
      } finally {
        if (!cancelled) setEdgeOutboxSummaryLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedIncident?.device_id, selectedIncident?.incident_id, viewDialogOpen]);

  const handleSaveNotificationTargets = React.useCallback(async () => {
    const { policyId, siteId, orgId } = policyScopeForEditing;
    if (!policyId || !siteId) {
      setNotificationEditorError('Detection rule context is missing. Open the full rule editor instead.');
      return;
    }
    const basePolicy = currentPolicyRecord || (await policiesApi.getPolicy(siteId, policyId, orgId || undefined));
    const existingActions = Array.isArray(basePolicy?.actions) ? basePolicy.actions.filter(Boolean) : [];
    const upsertAction = (type: string, enabled: boolean, params: Record<string, any>) => {
      const rest = existingActions.filter((action: any) => String(action?.type || '').trim() !== type);
      if (!enabled) return rest;
      return [
        ...rest,
        {
          ...(existingActions.find((action: any) => String(action?.type || '').trim() === type) || {}),
          type,
          enabled: true,
          params,
        },
      ];
    };
    try {
      setNotificationEditorBusy(true);
      setNotificationEditorError(null);
      let nextActions = existingActions.slice();
      nextActions = upsertAction('email.notify', Boolean(emailTargetDraft.trim()), {
        to: emailTargetDraft.trim(),
        subject: 'Safety incident notification',
      });
      nextActions = (() => {
        const rest = nextActions.filter((action: any) => String(action?.type || '').trim() !== 'sms.notify');
        if (!smsTargetDraft.trim()) return rest;
        return [...rest, { ...(existingActions.find((action: any) => String(action?.type || '').trim() === 'sms.notify') || {}), type: 'sms.notify', enabled: true, params: { to: smsTargetDraft.trim() } }];
      })();
      nextActions = (() => {
        const rest = nextActions.filter((action: any) => String(action?.type || '').trim() !== 'webhook.call');
        if (!webhookTargetDraft.trim()) return rest;
        return [...rest, { ...(existingActions.find((action: any) => String(action?.type || '').trim() === 'webhook.call') || {}), type: 'webhook.call', enabled: true, params: { url: webhookTargetDraft.trim(), method: 'POST' } }];
      })();
      await policiesApi.updatePolicy(siteId, policyId, { actions: nextActions }, orgId || undefined);
      if (notificationEditorDeployNow) {
        await policiesApi.deployPolicy(siteId, policyId);
      }
      setNotificationEditorOpen(false);
    } catch (error: any) {
      setNotificationEditorError(error?.response?.data?.error || error?.message || 'Failed to save notification targets');
    } finally {
      setNotificationEditorBusy(false);
    }
  }, [currentPolicyRecord, emailTargetDraft, notificationEditorDeployNow, policyScopeForEditing, smsTargetDraft, webhookTargetDraft]);
  const openRelatedIncidents = () => {
    if (typeof onViewPatternOccurrences === 'function') {
      onViewPatternOccurrences();
      return;
    }
    const source: any = selectedGroupContext || selectedIncident;
    if (!source) return;
    setTableMode('raw');
    if (source?.incident_type) setFilterIncidentType(String(source.incident_type));
    if (source?.zone_id) setFilterZoneId(String(source.zone_id));
    if (source?.camera_id) setFilterCameraId(String(source.camera_id));
    if (source?.device_id) setFilterDeviceId(String(source.device_id));
    setViewDialogOpen(false);
  };

  const evidenceCount =
    evidenceAttachments.length ||
    (hasSnapshotArtifact ? 1 : 0) +
      (hasClipArtifact ? 1 : 0);

  const groupedRepresentativeReviewComplete = Boolean(
    selectedIncident && isExplicitRepresentativeReviewComplete(selectedIncident as Record<string, unknown>),
  );
  const groupedRepresentativeReviewOutcome = selectedIncident
    ? readRepresentativeReviewOutcome(selectedIncident as Record<string, unknown>)
    : '';
  const observationParentCaseIncidentId = resolveParentCaseIncidentId(
    (selectedGroupContext as Record<string, unknown> | null) || null,
    selectedIncident,
  );
  const observationNextIncidentId = resolveNextObservationIncidentId(
    String(selectedIncident?.incident_id || '').trim(),
    observationReviewQueueIncidents,
  );
  const observationBackToCaseLabel = observationBackToCaseButtonLabel(
    Math.max(
      observationParentCaseCount,
      relatedIncidentCount,
      patternVolumeCount,
    ),
  );

  const reviewerActionPanel = selectedIncident ? (
    <IncidentReviewerActionPanel
      selectedIncident={selectedIncident}
      canManageIncidentWorkflow={canManageIncidentWorkflow}
      canCommentOnIncident={canCommentOnIncident}
      canUseOpsAutomation={canUseOpsAutomation}
      followUpSignal={followUpSignal}
      drawerIncidentLoading={drawerIncidentLoading}
      drawerBusy={drawerBusy}
      groupedReviewMode={groupedReviewMode}
      observationDispositionMode={observationDispositionMode}
      selectedGroupContext={(selectedGroupContext as Record<string, unknown> | null) || null}
      reviewWindowContext={reviewWindowContext}
      followUpOwnerLabel={followUpOwnerSummary}
      resolveTargetIncidentId={String(selectedIncident.incident_id || '').trim()}
      observationReviewComplete={groupedRepresentativeReviewComplete}
      observationReviewOutcome={groupedRepresentativeReviewOutcome}
      observationBackToCaseLabel={observationBackToCaseLabel}
      observationParentCaseIncidentId={observationParentCaseIncidentId}
      observationNextIncidentId={observationNextIncidentId}
      onOpenParentCaseReview={
        observationParentCaseIncidentId && onOpenReviewPageFullProof
          ? () => onOpenReviewPageFullProof(observationParentCaseIncidentId)
          : undefined
      }
      onOpenNextObservationReview={
        observationNextIncidentId && onOpenReviewPage
          ? () => onOpenReviewPage(observationNextIncidentId)
          : undefined
      }
      openResolveDialogWithOutcome={openResolveDialogWithOutcome}
      handleMarkFalsePositive={handleMarkFalsePositive}
      onAcknowledge={handleAcknowledge}
      onOpenDiagnostics={openDiagnostics}
      onResolve={() => setCloseDialogOpen(true)}
      onAddComment={handleAddCommentShortcut}
      onStartReview={handleMarkInReview}
      onCreateWorkOrder={handleEnsureWorkOrder}
      onSyncExternal={handleSyncToOdoo}
    />
  ) : null;
  const drawerCardSx = {
    p: 2.1,
    mb: 2,
    borderRadius: 3,
    borderColor: '#d0d7e2',
    bgcolor: '#fbfcfe',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  } as const;
  const cardHeadingSx = { fontSize: 17, fontWeight: 900 } as const;
  const expandGroupedGuidedPanelRef = React.useRef<(panelId: GroupedGuidedPanelId) => void>();
  const appliedReviewFocusKeyRef = React.useRef('');
  const registerGroupedGuidedPanelControl = React.useCallback<
    NonNullable<GroupedIncidentGuidedWorkflowProps['onRegisterPanelControl']>
  >((control) => {
    expandGroupedGuidedPanelRef.current = control.expand;
  }, []);
  const expandGroupedGuidedPanel = React.useCallback((panelId: GroupedGuidedPanelId) => {
    expandGroupedGuidedPanelRef.current?.(panelId);
  }, []);
  const focusGroupedGuidedPanel = React.useCallback((panelId: GroupedGuidedPanelId) => {
    expandGroupedGuidedPanel(panelId);
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        const panel = document.querySelector(
          `[data-testid="grouped-guided-panel-${panelId}"]`,
        );
        if (panel instanceof HTMLElement) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 120);
    }
  }, [expandGroupedGuidedPanel]);
  const focusAssignActionOwnerForm = React.useCallback(() => {
    focusGroupedGuidedPanel('decide_action');
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        const form = document.querySelector('[data-testid="grouped-assign-action-owner-form"]');
        if (form instanceof HTMLElement) {
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 160);
    }
  }, [focusGroupedGuidedPanel]);
  const focusReviewBlocker = React.useCallback((focus: string) => {
    const normalizedFocus = safeText(focus).toLowerCase();
    if (!normalizedFocus) return;
    if (groupedReviewMode) {
      if (normalizedFocus === 'evidence_review') {
        focusGroupedGuidedPanel('verify_evidence');
        return;
      }
      if (normalizedFocus === 'action_owner') {
        focusAssignActionOwnerForm();
        return;
      }
      if (normalizedFocus === 'corrective_action') {
        focusGroupedGuidedPanel('decide_action');
        return;
      }
      if (normalizedFocus === 'impact_measurement') {
        focusGroupedGuidedPanel('track_impact');
        return;
      }
      if (normalizedFocus === 'final_signoff') {
        focusGroupedGuidedPanel('report');
        return;
      }
    }

    const stepByFocus: Partial<Record<string, JourneyStepId>> = {
      evidence_review: 'step2',
      action_owner: 'step7',
      corrective_action: 'step5',
      impact_measurement: 'step6',
      final_signoff: 'step8',
    };
    const stepId = stepByFocus[normalizedFocus];
    if (!stepId) return;
    setActiveJourneyStep(stepId);
    scrollToJourneyStep(stepId);
  }, [
    focusAssignActionOwnerForm,
    focusGroupedGuidedPanel,
    groupedReviewMode,
    scrollToJourneyStep,
  ]);
  React.useEffect(() => {
    if (!viewDialogOpen || !reviewFocus) {
      if (!reviewFocus) appliedReviewFocusKeyRef.current = '';
      return;
    }
    const focusKey = `${reviewFocus}:${selectedIncident?.incident_id || ''}:${selectedGroupKey || ''}`;
    if (appliedReviewFocusKeyRef.current === focusKey) return;
    const timer = window.setTimeout(() => {
      appliedReviewFocusKeyRef.current = focusKey;
      focusReviewBlocker(reviewFocus);
    }, groupedReviewMode ? 180 : 120);
    return () => window.clearTimeout(timer);
  }, [
    focusReviewBlocker,
    groupedReviewMode,
    reviewFocus,
    selectedGroupKey,
    selectedIncident?.incident_id,
    viewDialogOpen,
  ]);
  const openDeepReviewChecklist = React.useCallback(() => {
    if (groupedReviewMode) {
      focusGroupedGuidedPanel('verify_evidence');
      return;
    }
    setGroupedDeepReviewOpen(true);
  }, [focusGroupedGuidedPanel, groupedReviewMode]);
  React.useEffect(() => {
    if (!groupedReviewMode || typeof registerOccurrenceOpenedHandler !== 'function') return;
    registerOccurrenceOpenedHandler(() => {
      // Expand evidence in place. Do not scrollIntoView — the user is already at the heatmap.
      expandGroupedGuidedPanel('verify_evidence');
    });
    return () => registerOccurrenceOpenedHandler(null);
  }, [expandGroupedGuidedPanel, groupedReviewMode, registerOccurrenceOpenedHandler]);
  const patternReviewPrimaryHref = readPatternReviewPrimaryHref(riskPatternWorkflowReviewActions);
  const patternReviewFocusActions = React.useMemo(() => (
    bindPatternReviewActionsToScanOrEvidence(
      riskPatternWorkflowReviewActions,
      patternReviewPrimaryHref,
      () => {
        appliedReviewFocusKeyRef.current = `evidence_review:${selectedIncident?.incident_id || ''}:${selectedGroupKey || ''}`;
        focusGroupedGuidedPanel('verify_evidence');
      },
    )
  ), [
    focusGroupedGuidedPanel,
    patternReviewPrimaryHref,
    riskPatternWorkflowReviewActions,
    selectedGroupKey,
    selectedIncident?.incident_id,
  ]);
  const scrollToPatternDecision = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  const renderAuditStepHeading = React.useCallback(
    (stepId: string, fallbackTitle: string, extraSubtitle?: string) => {
      const spec = groupedStepTitle(stepId, groupedReviewMode, observationDispositionMode);
      const title = spec.title || fallbackTitle;
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: spec.subtitle || extraSubtitle ? 0.6 : 1.1 }}>
            <Typography component="span" sx={cardHeadingSx}>
              {title}
            </Typography>
            {spec.badge ? <AuditScopeBadge label={spec.badge} /> : null}
          </Box>
          {spec.subtitle || extraSubtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1, lineHeight: 1.55 }}>
              {spec.subtitle || extraSubtitle}
            </Typography>
          ) : null}
        </>
      );
    },
    [groupedReviewMode, observationDispositionMode],
  );
  const cardLabelSx = {
    fontSize: 10.5,
    letterSpacing: 0.95,
    color: 'text.secondary',
    display: 'block',
    textTransform: 'uppercase',
    fontWeight: 800,
  } as const;
  const stepSectionSx = (stepId: JourneyStepId) => ({
    scrollMarginTop: 88,
    borderRadius: 3,
    transition: 'box-shadow 160ms ease, transform 160ms ease',
    boxShadow:
      activeJourneyStep === stepId
        ? '0 0 0 2px rgba(37, 99, 235, 0.18), 0 14px 28px rgba(15, 23, 42, 0.10)'
        : 'none',
  });
  const handleHeatmapCellSelect = React.useCallback(
    ({ dimension, id, hour }: { dimension: 'zone' | 'camera'; id: string; hour: number }) => {
      if (groupedReviewMode && typeof onGroupedHeatmapCellSelect === 'function') {
        void onGroupedHeatmapCellSelect({ dimension, id, hour });
        return;
      }
      const params = new URLSearchParams();
      params.set('table_mode', 'raw');
      params.set('heatmap_hour', String(hour));
      if (safeText(selectedIncident?.incident_type)) params.set('incident_type', safeText(selectedIncident?.incident_type));
      if (dimension === 'zone' && id !== '__unassigned__') params.set('zone_id', id);
      if (dimension === 'camera' && id !== '__unassigned__') params.set('camera_id', id);
      navigate(`/incidents?${params.toString()}`);
    },
    [groupedReviewMode, navigate, onGroupedHeatmapCellSelect, selectedIncident?.incident_type],
  );
  const groupedHeatmapCard = selectedIncident ? (
    <RecurringRiskHeatmapCard
      data={heatmapData}
      dimension="zone"
      incidentType={safeText(selectedIncident?.incident_type)}
      incidentTypeOptions={
        safeText(selectedIncident?.incident_type)
          ? [{ value: safeText(selectedIncident?.incident_type), label: currentTitle }]
          : []
      }
      zoneNameById={zoneNameById}
      cameraNameById={cameraNameById}
      onDimensionChange={() => {}}
      onIncidentTypeChange={() => {}}
      onCellSelect={handleHeatmapCellSelect}
      windowDays={heatmapWindowDays}
      onWindowDaysChange={setHeatmapWindowDays}
      showScopeControls={false}
      openingOccurrence={heatmapOpening}
      cellSelectCaption={
        groupedReviewMode && groupedHeatmapCellSelectCaption
          ? String(groupedHeatmapCellSelectCaption)
          : undefined
      }
    />
  ) : null;
  const groupedHeatmapBucketCount = React.useMemo(
    () => (heatmapData?.rows || []).reduce((sum, row) => sum + Math.max(0, Number(row?.total_count || 0)), 0),
    [heatmapData],
  );
  const groupedHeatmapSummaryText = React.useMemo(
    () => patternBucketSummaryText(reviewWindowDisplayCount, groupedHeatmapBucketCount, 'review_window'),
    [groupedHeatmapBucketCount, reviewWindowDisplayCount],
  );
  const groupedSeriesSummaryText = '';
  const groupedEvidenceConfirmed = groupedRepresentativeReviewOutcome === 'true_event';
  const groupedCorrectiveActionIsLocked = groupedCorrectiveActionLocked({
    representativeReviewComplete: groupedRepresentativeReviewComplete,
    evidenceConfirmed: groupedEvidenceConfirmed,
  });
  const groupedImpactMeasurementWaived = selectedIncident
    ? readImpactMeasurementWaived(selectedIncident as Record<string, unknown>)
    : false;
  const groupedFollowUpMeasurementRecorded = selectedIncident
    ? isExplicitFollowUpMeasurementRecorded(selectedIncident as Record<string, unknown>)
    : false;
  const groupedTrackImpactReady = selectedIncident
    ? (
      (Array.isArray(workflowCorrectiveActions?.active_actions) && workflowCorrectiveActions.active_actions.length > 0)
      || (Array.isArray(workflowCorrectiveActions?.completed_actions) && workflowCorrectiveActions.completed_actions.length > 0)
      || isTrackImpactMeasurementReady({
          followUpOwnerLabel: followUpOwnerSummary,
          opsState: opsStateDraft,
          incidentStatus: String(selectedIncident.status || ''),
          hasWorkOrder: Boolean((selectedIncident as any)?.odoo_ticket_id),
          hasNextReview: Boolean(
            nextReviewAtValue
            || safeText((selectedIncident as any)?.sla_target_at)
            || safeText((selectedIncident as any)?.next_review_at),
          ),
        })
    )
    : false;
  const groupedFollowUpStarted = selectedIncident
    ? (
      ['acknowledged', 'in_progress', 'resolved', 'closed'].includes(
        String(opsStateDraft || selectedIncident?.ops_state || '').trim().toLowerCase(),
      )
      || Boolean((selectedIncident as any)?.odoo_ticket_id)
      || (Array.isArray(workflowCorrectiveActions?.active_actions)
        && workflowCorrectiveActions.active_actions.some((action: WorkflowCorrectiveAction) =>
          ['started', 'completed', 'validated'].includes(
            String(action?.status || '').trim().toLowerCase(),
          ),
        ))
      || (Array.isArray(workflowCorrectiveActions?.completed_actions)
        && workflowCorrectiveActions.completed_actions.length > 0)
    )
    : false;
  const claimReadinessControls = React.useMemo(
    () => (Array.isArray(workflowClaimReadiness?.controls) ? workflowClaimReadiness.controls : []),
    [workflowClaimReadiness?.controls],
  );
  const hasCanonicalClaimReadiness = React.useMemo(
    () => claimReadinessControls.length > 0 || Number(workflowClaimReadiness?.total_count || 0) > 0,
    [claimReadinessControls.length, workflowClaimReadiness?.total_count],
  );
  const groupedFollowUpMeasured =
    groupedFollowUpMeasurementRecorded
    || (hasCanonicalClaimReadiness
      ? isCanonicalClaimControlResolved(claimReadinessControls, 'matched_window_followup_validation')
      : workflowImpactSummary?.measurement_status === 'sufficient_data');
  const derivedGroupedCorrectiveWorkflowState = deriveGroupedCorrectiveWorkflowState({
    incident: groupedReviewMode ? undefined : selectedIncident as Record<string, unknown> | undefined,
    evidenceConfirmed: groupedEvidenceConfirmed,
    followUpOwnerLabel: followUpOwnerSummary,
    actionType: correctiveActionTypeDraft,
    priority: correctiveActionPriorityDraft,
    verificationMethod: correctiveActionVerificationMethodDraft,
    nextReviewDate: correctiveActionDueDateDraft,
    opsStateDraft,
    workflowCorrectiveActions,
  });
  const groupedCorrectiveActionPlanSaved = derivedGroupedCorrectiveWorkflowState.planSaved;
  const groupedCorrectivePlanDraftDirty = derivedGroupedCorrectiveWorkflowState.planDraftDirty;
  const groupedCorrectiveWorkflowPhase = derivedGroupedCorrectiveWorkflowState.phase;
  const groupedCorrectiveActionComplete = derivedGroupedCorrectiveWorkflowState.complete;
  const groupedEvidenceGateMissingItems = React.useMemo(
    () => buildGroupedEvidenceGateMissingItems({
      representativeReviewComplete: groupedRepresentativeReviewComplete,
      evidenceConfirmed: groupedEvidenceConfirmed,
      patternOwnerLabel: groupedReviewModel.representative.currentPatternOwner,
    }),
    [
      groupedEvidenceConfirmed,
      groupedRepresentativeReviewComplete,
      groupedReviewModel.representative.currentPatternOwner,
    ],
  );
  const canonicalClaimMissingItems = React.useMemo(
    () => buildCanonicalClaimReadinessMissingItems(claimReadinessControls),
    [claimReadinessControls],
  );
  const hasLinkedSafetyCase = patternOwnershipHasLinkedCase(
    riskPatternWorkflowReviewContext?.safetyCaseId,
  );
  const pendingObservationVerificationCount = Number(
    riskPatternWorkflowReviewContext?.pendingObservationVerificationCount,
  ) || 0;
  const groupedWorkflowProgress = React.useMemo(
    () => buildPatternOwnedWorkflowProgress({
      hasLinkedSafetyCase,
      hasSelectedRecurringRisk: Boolean(selectedGroupKey && selectedIncident),
      representativeReviewComplete: groupedRepresentativeReviewComplete,
      evidenceConfirmed: groupedEvidenceConfirmed,
      reviewOutcome: groupedRepresentativeReviewOutcome,
    }),
    [
      groupedEvidenceConfirmed,
      groupedRepresentativeReviewComplete,
      groupedRepresentativeReviewOutcome,
      hasLinkedSafetyCase,
      selectedGroupKey,
      selectedIncident,
    ],
  );
  const [groupedLocalViewedAt, setGroupedLocalViewedAt] = React.useState<string | undefined>();
  const [groupedGuidedInitialPanelId, setGroupedGuidedInitialPanelId] = React.useState<
    GroupedGuidedPanelId | undefined
  >();
  const [groupedExpandedPanelId, setGroupedExpandedPanelId] = React.useState<
    GroupedGuidedPanelId | false
  >(false);
  React.useEffect(() => {
    if (!groupedReviewMode || !selectedGroupKey) {
      setGroupedLocalViewedAt(undefined);
      setGroupedGuidedInitialPanelId(undefined);
      return;
    }
    const legacyViewedAt = readLegacyRiskContextViewedAt(selectedGroupKey);
    const ui = loadGroupedReviewUiSnapshot(selectedGroupKey);
    if (legacyViewedAt && !ui.risk_context_viewed_at) {
      saveGroupedReviewUiSnapshot(selectedGroupKey, { risk_context_viewed_at: legacyViewedAt });
      ui.risk_context_viewed_at = legacyViewedAt;
    }
    setGroupedLocalViewedAt(ui.risk_context_viewed_at);
    const restoredPanel = resolveGroupedGuidedInitialPanelId(ui, {
      representativeReviewComplete: groupedRepresentativeReviewComplete,
    });
    setGroupedGuidedInitialPanelId(restoredPanel);
    if (restoredPanel) {
      setGroupedExpandedPanelId(restoredPanel);
    }
    // groupedRepresentativeReviewComplete is sampled when the group opens so later
    // hydration cannot re-expand panels while the user is moving around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupedReviewMode,
    selectedGroupKey,
    selectedIncident?.incident_id,
  ]);
  React.useEffect(() => {
    if (!groupedReviewMode || !selectedGroupKey || !selectedIncident?.incident_id) return;
    saveGroupedReviewResumeSnapshot({
      groupKey: selectedGroupKey,
      representativeIncidentId: selectedIncident.incident_id,
      expandedPanel: groupedGuidedInitialPanelId,
    });
  }, [
    groupedGuidedInitialPanelId,
    groupedReviewMode,
    selectedGroupKey,
    selectedIncident?.incident_id,
  ]);
  const groupedLocalProgressHint = React.useMemo(
    () => formatGroupedReviewLocalViewHint(groupedLocalViewedAt),
    [groupedLocalViewedAt],
  );
  const handleClearGroupedLocalProgress = React.useCallback(() => {
    if (!selectedGroupKey) return;
    clearGroupedReviewUiSnapshot(selectedGroupKey);
    setGroupedLocalViewedAt(undefined);
  }, [selectedGroupKey]);
  const handleGroupedGuidedExpandedPanelChange = React.useCallback(
    (panelId: GroupedGuidedPanelId | false) => {
      setGroupedExpandedPanelId(panelId);
      if (!selectedGroupKey) return;
      saveGroupedReviewUiSnapshot(selectedGroupKey, {
        expanded_panel: panelId || '',
      });
      saveGroupedReviewResumeSnapshot({
        groupKey: selectedGroupKey,
        representativeIncidentId: selectedIncident?.incident_id,
        expandedPanel: panelId || '',
      });
    },
    [selectedGroupKey, selectedIncident?.incident_id],
  );
  const groupedRecommendedNextStepLabel = React.useMemo(
    () => buildPatternOwnedNextActionCopy({
      hasLinkedSafetyCase,
      hasSelectedRecurringRisk: Boolean(selectedGroupKey && selectedIncident),
      representativeReviewComplete: groupedRepresentativeReviewComplete,
      evidenceConfirmed: groupedEvidenceConfirmed,
      reviewOutcome: groupedRepresentativeReviewOutcome,
    }).body,
    [
      groupedEvidenceConfirmed,
      groupedRepresentativeReviewComplete,
      groupedRepresentativeReviewOutcome,
      hasLinkedSafetyCase,
      selectedGroupKey,
      selectedIncident,
    ],
  );
  const legacyGroupedReportSignOffMissingItems = React.useMemo(
    () => buildGroupedReportSignOffMissingItems({
      representativeReviewComplete: groupedRepresentativeReviewComplete,
      evidenceConfirmed: groupedEvidenceConfirmed,
      patternOwnerLabel: groupedReviewModel.representative.currentPatternOwner,
      followUpOwnerLabel: followUpOwnerSummary,
      correctiveActionComplete: groupedCorrectiveActionComplete,
      followUpMeasured: groupedFollowUpMeasured,
      impactWaived: groupedImpactMeasurementWaived || measurementImpactWaivedDraft,
    }),
    [
      groupedCorrectiveActionComplete,
      followUpOwnerSummary,
      groupedEvidenceConfirmed,
      groupedFollowUpMeasured,
      groupedImpactMeasurementWaived,
      groupedRepresentativeReviewComplete,
      groupedReviewModel.representative.currentPatternOwner,
      measurementImpactWaivedDraft,
    ],
  );
  const groupedReportSignOffMissingItems = React.useMemo(
    () => mergeGroupedSignOffMissingItems({
      evidenceGateItems: groupedEvidenceGateMissingItems,
      canonicalClaimItems: canonicalClaimMissingItems,
      legacyItems: legacyGroupedReportSignOffMissingItems,
      useCanonicalClaimReadiness: hasCanonicalClaimReadiness,
    }),
    [
      canonicalClaimMissingItems,
      groupedEvidenceGateMissingItems,
      hasCanonicalClaimReadiness,
      legacyGroupedReportSignOffMissingItems,
    ],
  );
  const groupedReportReadinessPresentation = React.useMemo(
    () => buildGroupedReportReadinessPresentation({ signOffMissingItems: groupedReportSignOffMissingItems }),
    [groupedReportSignOffMissingItems],
  );
  const groupedReportBlocked = hasCanonicalClaimReadiness
    ? groupedReportSignOffMissingItems.length > 0
    : groupedReportReadinessPresentation.blocked;
  const groupedReportBlockedReason = groupedReportReadinessPresentation.blockedReason;
  const groupedReportReadinessLabel = groupedReportReadinessPresentation.readinessLabel;
  const groupedReportReadinessHelper = groupedReportReadinessPresentation.readinessHelper;
  const groupedNextRequiredAction = React.useMemo(
    () => buildPatternOwnedNextActionCopy({
      hasLinkedSafetyCase,
      hasSelectedRecurringRisk: Boolean(selectedGroupKey && selectedIncident),
      representativeReviewComplete: groupedRepresentativeReviewComplete,
      evidenceConfirmed: groupedEvidenceConfirmed,
      reviewOutcome: groupedRepresentativeReviewOutcome,
    }),
    [
      groupedEvidenceConfirmed,
      groupedRepresentativeReviewComplete,
      groupedRepresentativeReviewOutcome,
      hasLinkedSafetyCase,
      selectedGroupKey,
      selectedIncident,
    ],
  );
  const patternNextActionButtons = patternOwnedNextActionButtons({
    hasLinkedSafetyCase,
    representativeReviewComplete: groupedRepresentativeReviewComplete,
    evidenceConfirmed: groupedEvidenceConfirmed,
    hasNewActivityReview: afterSignedScanReviewActive,
    hasCreateSafetyCase: Boolean(riskPatternWorkflowReviewActions?.onCreateSafetyCase),
    hasOpenSafetyCase: Boolean(riskPatternWorkflowReviewActions?.onOpenSafetyCase),
    pendingObservationVerificationCount,
    reviewOutcome: groupedRepresentativeReviewOutcome,
  });
  const patternNextActionButton = patternNextActionButtons.primary;
  const patternSecondaryActionButton = patternNextActionButtons.secondary;
  const patternInlineReviewActions = omitDuplicatePatternReviewCtas(
    patternReviewFocusActions,
    patternNextActionButton?.action,
  );
  const navigatePatternVerifyObservations = React.useCallback(() => {
    const scanPath = String(riskPatternWorkflowReviewActions?.currentScanReviewPath || '').trim();
    if (scanPath) {
      navigate(scanPath);
      return;
    }
    const caseId = String(riskPatternWorkflowReviewContext?.safetyCaseId || '').trim();
    if (!caseId) return;
    navigate(buildSafetyCaseVerifyPath({
      safetyCaseId: caseId,
      orgId: String(activeScope?.orgId || selectedIncident?.org_id || '').trim() || null,
      siteId: String(scopedSiteId || activeScope?.siteId || selectedIncident?.site_id || '').trim() || null,
      workflowId,
      incidentId: String(selectedIncident?.incident_id || '').trim() || null,
    }));
  }, [
    activeScope?.orgId,
    activeScope?.siteId,
    navigate,
    riskPatternWorkflowReviewContext?.safetyCaseId,
    riskPatternWorkflowReviewActions?.currentScanReviewPath,
    scopedSiteId,
    selectedIncident?.incident_id,
    selectedIncident?.org_id,
    selectedIncident?.site_id,
    workflowId,
  ]);
  const groupedPrimaryLocationLine = groupedScopeSummary
    .split(' · ')
    .slice(0, 1)
    .filter(Boolean)
    .join(' · ');
  const riskPatternReviewContextPanel = !observationDispositionMode
    && (selectedIncident || selectedGroupContext)
    && (riskPatternWorkflowReviewContext || riskPatternWorkflowReviewLoading || riskPatternWorkflowReviewError) ? (
    <RiskPatternWorkflowReviewContextPanel
      context={riskPatternWorkflowReviewContext}
      loading={riskPatternWorkflowReviewLoading}
      error={riskPatternWorkflowReviewError}
      actions={patternInlineReviewActions}
      afterSignedScanTimeline={afterSignedScanTimeline}
      scanWindowLabel={formatReviewWindowHeaderValue(reviewWindowContext, siteTimeZone)}
      scheduledScanSiteId={String(scopedSiteId || selectedIncident?.site_id || '').trim() || null}
      evidenceConfirmed={groupedEvidenceConfirmed}
      representativeReviewComplete={groupedRepresentativeReviewComplete}
      siteTimeZone={siteTimeZone}
    />
  ) : null;
  const workflowReviewContextLead = isPageMode
    ? riskPatternReviewContextPanel
    : null;

  const groupedReviewLead = groupedReviewMode && selectedIncident ? (
    <>
      {!isPageMode ? riskPatternReviewContextPanel : null}
      {showAfterSignedScanReviewPanel ? (
        <AfterSignedScanReviewPanel
          context={afterSignedScanReview}
          reviewWindowContext={reviewWindowContext}
          scopedActivityCount={afterSignedScanScopedActivityCount}
          latestObservation={afterSignedScanLatestObservation}
          reviewWindowEnd={reviewTimeRange?.toDate || reviewWindowContext.toIso}
          reviewState={afterSignedScanReview.newActivityReviewState}
          linkedSafetyCaseTitle={riskPatternWorkflowReviewContext?.linkedSafetyCaseTitle}
          safetyCaseId={riskPatternWorkflowReviewContext?.safetyCaseId}
          originatingRiskPattern={riskPatternWorkflowReviewContext?.originatingRiskPattern}
          caseStateLabel={riskPatternWorkflowReviewContext?.caseStateLabel}
          onTimelineChange={onAfterSignedScanTimelineChange}
          onResolveStartBeforeSignedScan={onResolveAfterSignedScanStartBeforeSignedScan}
          onOpenSafetyCase={patternInlineReviewActions?.onOpenSafetyCase}
          onViewSignedSafetyScan={patternInlineReviewActions?.onViewSignedSafetyScan}
          onReviewNewActivity={patternInlineReviewActions?.onReviewNewActivity}
          reviewNewActivityHref={patternInlineReviewActions?.onReviewNewActivity
            ? patternReviewPrimaryHref
            : undefined}
          onCreateUpdatedSafetyScan={riskPatternWorkflowReviewActions?.onCreateUpdatedSafetyScan}
          onReviewFullHistory={typeof onReviewFullSafetyCaseHistory === 'function'
            ? onReviewFullSafetyCaseHistory
            : undefined}
          siteTimeZone={siteTimeZone}
        />
      ) : null}
      <PatternDispositionSummaryCard
        summary={patternDispositionSummary}
        onOpenReviewSeriesQueue={openRelatedIncidents}
      />
      <GroupedIncidentReviewWorkspace
      title={currentTitle}
      workflowLabel={workflowLabel || 'Needs review'}
      evidenceStatusLabel={groupedEvidenceStatusLabel}
      patternVolumeCount={reviewWindowDisplayCount}
      patternVolumeKind={patternVolumeKind}
      qualifyingEntryCount={hotspotOperationalMetrics.qualifyingEntryCount}
      occupancyDurationMs={hotspotOperationalMetrics.occupancyDurationMs}
      forkliftCoPresenceEntryCount={hotspotOperationalMetrics.forkliftCoPresenceEntryCount}
      representativeIncidentId={representativeIncidentId}
      currentIncidentId={String(heatmapViewingIncidentId || selectedIncident?.incident_id || '').trim() || undefined}
      recurrenceLabel={recurringIssueSummary !== 'No longer-term recurrence trend reported' ? recurringIssueSummary : riskPatternLabel}
      firstSeenLabel={groupedFirstSeenLabel}
      lastSeenLabel={groupedLastSeenLabel}
      seriesSummaryText={groupedSeriesSummaryText}
      whatToKnow={groupedWhatToKnow}
      patternOwner={groupedReviewModel.representative.currentPatternOwner}
      followUpOwner={followUpOwnerSummary}
      onViewRelatedIncidents={openRelatedIncidents}
      nextStepLabel={groupedRecommendedNextStepLabel}
      primaryLocationLabel={groupedPrimaryLocationLine || recurringIssueSummary}
      reportReadinessLabel={groupedReportReadinessLabel}
      reportReadinessHelper={groupedReportReadinessHelper}
      evidenceReviewIsCurrent={groupedWorkflowProgress.currentStepId === 'review'}
      currentWorkflowStageLabel={patternOwnedCurrentStageLabel({
        hasLinkedSafetyCase,
        hasSelectedRecurringRisk: Boolean(selectedGroupKey && selectedIncident),
        representativeReviewComplete: groupedRepresentativeReviewComplete,
        evidenceConfirmed: groupedEvidenceConfirmed,
        reviewOutcome: groupedRepresentativeReviewOutcome,
      })}
      containPatternReportLifecycle
      blockerSummary=""
      nextRequiredActionTitle={groupedNextRequiredAction.title}
      nextRequiredActionBody={groupedNextRequiredAction.body}
      nextRequiredActionSecondary={groupedNextRequiredAction.secondaryText}
      nextRequiredActionButtonLabel={patternNextActionButton?.label}
      nextRequiredActionHref={resolvePatternReviewPrimaryHref(
        patternNextActionButton?.action,
        patternReviewPrimaryHref,
      )}
      onNextRequiredActionButton={resolvePatternReviewPrimaryHandler({
        action: patternNextActionButton?.action,
        currentScanReviewPath: patternReviewPrimaryHref,
        actions: patternReviewFocusActions,
        onVerifyObservations: navigatePatternVerifyObservations,
        onScrollPatternEvidence: () => focusGroupedGuidedPanel('verify_evidence'),
      })}
      nextRequiredActionSecondaryButtonLabel={patternSecondaryActionButton?.label}
      onNextRequiredActionSecondaryButton={resolvePatternReviewPrimaryHandler({
        action: patternSecondaryActionButton?.action,
        currentScanReviewPath: patternReviewPrimaryHref,
        actions: patternReviewFocusActions,
        onVerifyObservations: navigatePatternVerifyObservations,
        onScrollPatternEvidence: () => focusGroupedGuidedPanel('verify_evidence'),
      })}
      representativeEventHelper={GROUPED_REPRESENTATIVE_EVENT_HELPER}
      reviewWindowContext={reviewWindowContext}
      afterSignedScanTimeline={afterSignedScanTimeline}
      siteTimeZone={siteTimeZone}
    />
    </>
  ) : null;
  const groupedPatternDecisionCard = groupedReviewMode && selectedIncident ? (
    <PatternDecisionCard
      variant="understand_risk"
      group={(selectedGroupContext as IncidentGroup) || null}
      patternVolumeCount={patternVolumeCount}
      patternVolumeKind={patternVolumeKind}
      patternVolumeDisplay={pattern.patternVolumeDisplay}
      patternVolumeBadge={pattern.patternVolumeBadge}
      reviewWindowContext={reviewWindowContext}
      currentPatternOwner={groupedReviewModel.representative.currentPatternOwner}
      patternAssignedBy={readPatternAssignedByLabel(selectedIncident)}
      followUpOwnerLabel={followUpOwnerSummary}
      workflowLabel={workflowLabel || 'Needs review'}
      groupingBasis={normalizeGroupingBasis(groupedReasonText) || GROUPED_INCIDENT_GROUPING_BASIS_FALLBACK}
      scopeSummary={groupedScopeSummary}
      policyLabel={groupedPolicyLabel}
      recurrenceLabel={recurringIssueSummary !== 'No longer-term recurrence trend reported' ? recurringIssueSummary : riskPatternLabel}
      summaryText={groupedSummaryText}
      actionScopeBaseline={groupedActionScope.baselineLine}
      actionScopeLine={groupedActionScope.actionScopeLine}
      canManagePatternActions={canManageGroupPatterns}
      groupPatternActionBusy={groupPatternActionBusy}
      actionAvailabilityMessage={patternActionAvailabilityMessage}
      onGroupPatternAction={onGroupPatternAction}
    />
  ) : null;
  const handleSaveFollowupOwnerForGrouped = React.useCallback(
    async (owner?: string) => {
      const saved = await handleSaveFollowupOwner(owner);
      if (!saved) return;
      if (selectedGroupKey) {
        saveGroupedReviewUiSnapshot(selectedGroupKey, { expanded_panel: 'decide_action' });
      }
      expandGroupedGuidedPanelRef.current?.('decide_action');
      setGroupedExpandedPanelId('decide_action');
    },
    [handleSaveFollowupOwner, selectedGroupKey],
  );
  const groupedAssignActionPanel = groupedReviewMode && selectedIncident ? (
    <GroupedAssignActionPanel
      followUpOwnerLabel={followUpOwnerSummary}
      actionOwnerDraft={opsAssigneeDraft}
      onActionOwnerDraftChange={setOpsAssigneeDraft}
      onSaveActionOwner={handleSaveFollowupOwnerForGrouped}
      canSaveActionOwner={Boolean(
        selectedIncident?.incident_id
        && (canManageIncidentWorkflow || canUseOpsAutomation)
        && !groupedCorrectiveActionIsLocked,
      )}
      actionOwnerSaveBusy={drawerBusy === 'ops-owner'}
      ownerSaveFeedback={
        workflowFeedback && !String(workflowFeedback.message || '').includes('Corrective action plan')
          ? workflowFeedback
          : null
      }
    />
  ) : null;
  const groupedHeatmapSection = groupedReviewMode && selectedIncident ? (
    <GroupedIncidentHeatmapSection
      sectionTitle={UNDERSTAND_RISK_HEATMAP_TITLE}
      sectionHelper={UNDERSTAND_RISK_HEATMAP_HELPER}
      showCellNavigationHint
      heatmapSummaryText={groupedHeatmapSummaryText}
      heatmapLoading={heatmapLoading}
      heatmapError={heatmapError}
      heatmapPageFeedback={heatmapPageFeedback}
      heatmapCard={groupedHeatmapCard}
    />
  ) : null;
  const groupedUnderstandRiskLocationLine = groupedPrimaryLocationLine;
  const journeyStepIds = React.useMemo(() => journeySteps.map((step) => step.id), [journeySteps]);
  const nextIncompleteJourneyStep = React.useMemo<JourneyStepId>(() => {
    return journeyStepIds.find((stepId) => !completedJourneySteps[stepId]) || journeyStepIds[journeyStepIds.length - 1] || journeyInitialStep;
  }, [completedJourneySteps, journeyInitialStep, journeyStepIds]);
  const journeyPhases = React.useMemo(
    () => {
      if (observationDispositionMode) {
        return [
          {
            id: 'verify' as const,
            title: 'Review',
            stepIds: ['step1', 'step2'] as JourneyStepId[],
          },
          {
            id: 'decide' as const,
            title: 'Disposition',
            stepIds: ['step5'] as JourneyStepId[],
          },
        ];
      }
      return [
        {
          id: 'detect' as const,
          title: 'Detected',
          stepIds: selectedGroupKey && groupedReviewMode ? (['step0', 'step1'] as JourneyStepId[]) : (['step1'] as JourneyStepId[]),
        },
        {
          id: 'verify' as const,
          title: 'Review',
          stepIds: ['step2', 'step3', 'step4'] as JourneyStepId[],
        },
        {
          id: 'decide' as const,
          title: 'Assign Action',
          stepIds: ['step5'] as JourneyStepId[],
        },
        {
          id: 'follow_up' as const,
          title: 'Impact Measurement',
          stepIds: ['step6', 'step7'] as JourneyStepId[],
        },
        {
          id: 'closeout' as const,
          title: 'Report',
          stepIds: ['step8'] as JourneyStepId[],
        },
      ];
    },
    [groupedReviewMode, observationDispositionMode, selectedGroupKey],
  );
  const activeJourneyPhase = React.useMemo<JourneyPhaseId>(() => {
    return (
      journeyPhases.find((phase) => phase.stepIds.includes(activeJourneyStep))?.id ||
      'detect'
    );
  }, [activeJourneyStep, journeyPhases]);
  const nextIncompleteJourneyPhase = React.useMemo<JourneyPhaseId>(() => {
    return journeyPhases.find((phase) => phase.stepIds.some((stepId) => !completedJourneySteps[stepId]))?.id || journeyPhases[journeyPhases.length - 1]?.id || 'detect';
  }, [completedJourneySteps, journeyPhases]);
  const getJourneyStepState = React.useCallback(
    (stepId: JourneyStepId): 'done' | 'in_view' | 'up_next' | 'later' => {
      if (completedJourneySteps[stepId]) return 'done';
      if (stepId === activeJourneyStep) return 'in_view';
      if (stepId === nextIncompleteJourneyStep) return 'up_next';
      return 'later';
    },
    [activeJourneyStep, completedJourneySteps, nextIncompleteJourneyStep],
  );
  const journeyStateMeta = (stepId: JourneyStepId) => {
    const state = getJourneyStepState(stepId);
    if (state === 'done') return { label: 'Done', color: 'success.main' };
    if (state === 'in_view') return { label: 'In view', color: 'primary.main' };
    if (state === 'up_next') return { label: 'Up next', color: 'warning.dark' };
    return { label: 'Later', color: 'text.secondary' };
  };
  const getJourneyPhaseState = React.useCallback(
    (phaseId: JourneyPhaseId): 'done' | 'in_view' | 'up_next' | 'later' => {
      const phase = journeyPhases.find((value) => value.id === phaseId);
      if (!phase) return 'later';
      if (phase.stepIds.every((stepId) => completedJourneySteps[stepId])) return 'done';
      if (phaseId === activeJourneyPhase) return 'in_view';
      if (phaseId === nextIncompleteJourneyPhase) return 'up_next';
      return 'later';
    },
    [activeJourneyPhase, completedJourneySteps, journeyPhases, nextIncompleteJourneyPhase],
  );
  const journeyPhaseMeta = (phaseId: JourneyPhaseId) => {
    const state = getJourneyPhaseState(phaseId);
    if (state === 'done') return { label: 'Done', color: 'success.main', buttonColor: 'success' as const };
    if (state === 'in_view') return { label: 'In view', color: 'primary.main', buttonColor: 'primary' as const };
    if (state === 'up_next') return { label: 'Up next', color: 'warning.dark', buttonColor: 'warning' as const };
    return { label: 'Later', color: 'text.secondary', buttonColor: 'inherit' as const };
  };
  const journeyPhaseForStep = React.useCallback(
    (stepId: JourneyStepId) => journeyPhases.find((phase) => phase.stepIds.includes(stepId)) || null,
    [journeyPhases],
  );
  const journeyPhaseTitleForStep = React.useCallback(
    (stepId: JourneyStepId) => journeyPhaseForStep(stepId)?.title || 'Review',
    [journeyPhaseForStep],
  );
  const handleToggleJourneyStep = React.useCallback(
    (stepId: JourneyStepId) => {
      const currentStep = journeySteps.find((step) => step.id === stepId);
      const phase = journeyPhaseForStep(stepId);
      const nextCompleted = !Boolean(completedJourneySteps[stepId]);
      const incidentId = safeText(selectedIncident?.incident_id);
      const nextCompletedStepIds = journeySteps
        .map((step) => step.id)
        .filter((id) => (id === stepId ? nextCompleted : Boolean(completedJourneySteps[id])));

      setCompletedJourneySteps((current) => ({
        ...current,
        [stepId]: !current[stepId],
      }));

      if (!incidentId || !currentStep) return;
      void Promise.resolve(
        auditApi.appendEvent({
          action: 'UPDATE',
          resource_type: 'incident',
          resource_id: incidentId,
          details: {
            action: nextCompleted ? 'review_journey_step_completed' : 'review_journey_step_reopened',
            audit_group: 'review_session',
            review_session_id: reviewJourneySessionId,
            render_mode: renderMode,
            step_id: stepId,
            step_title: currentStep.title,
            phase_id: phase?.id || null,
            phase_title: phase?.title || null,
            group_key: selectedGroupKey || null,
          },
          old_value: {
            review_journey: {
              step_id: stepId,
              completed: !nextCompleted,
            },
          },
          new_value: {
            review_journey: {
              step_id: stepId,
              completed: nextCompleted,
              completed_step_ids: nextCompletedStepIds,
            },
          },
          success: true,
        }),
      ).catch(() => {
        // Non-blocking: the operator workflow should continue even if audit append fails.
      });
    },
    [
      completedJourneySteps,
      journeyPhaseForStep,
      journeySteps,
      renderMode,
      reviewJourneySessionId,
      selectedGroupKey,
      selectedIncident?.incident_id,
    ],
  );
  const scrollToJourneyPhase = React.useCallback(
    (phaseId: JourneyPhaseId) => {
      const phase = journeyPhases.find((value) => value.id === phaseId);
      if (!phase || phase.stepIds.length === 0) return;
      scrollToJourneyStep(phase.stepIds[0]);
    },
    [journeyPhases, scrollToJourneyStep],
  );
  const handleGroupedGuidedPrimaryAction = React.useCallback(
    (panelId: GroupedGuidedPanelId) => {
      const phaseByPanel: Record<GroupedGuidedPanelId, JourneyPhaseId> = {
        understand_risk: 'detect',
        verify_evidence: 'verify',
        decide_action: 'decide',
        track_impact: 'follow_up',
        report: 'closeout',
      };
      if (panelId === 'understand_risk') {
        if (selectedGroupKey) {
          const viewedAt = new Date().toISOString();
          saveGroupedReviewUiSnapshot(selectedGroupKey, { risk_context_viewed_at: viewedAt });
          setGroupedLocalViewedAt(viewedAt);
        }
        expandGroupedGuidedPanelRef.current?.('verify_evidence');
        scrollToJourneyPhase('verify');
        return;
      }
      if (panelId === 'decide_action') {
        if (ownerRoleIsUnassigned(followUpOwnerSummary)) {
          focusAssignActionOwnerForm();
          return;
        }
        expandGroupedGuidedPanelRef.current?.('track_impact');
        scrollToJourneyPhase('follow_up');
        return;
      }
      if (panelId === 'track_impact') {
        expandGroupedGuidedPanelRef.current?.('report');
        scrollToJourneyPhase('closeout');
        return;
      }
      if (panelId === 'report' && typeof _openReportDialog === 'function') {
        _openReportDialog();
        return;
      }
      expandGroupedGuidedPanelRef.current?.(panelId);
      scrollToJourneyPhase(phaseByPanel[panelId]);
    },
    [
      _openReportDialog,
      focusAssignActionOwnerForm,
      followUpOwnerSummary,
      scrollToJourneyPhase,
      selectedGroupKey,
    ],
  );
  const handleGroupedGuidedSecondaryAction = React.useCallback((_panelId: GroupedGuidedPanelId) => {
    // Secondary panel footers removed — actions live inside each panel section.
  }, []);
  const groupedUnderstandRiskPanel = groupedReviewMode && selectedIncident ? (
    <GroupedUnderstandRiskPanel
      riskPatternSummary={(
        <GroupedRiskPatternSummary
          riskPattern={buildRecurringRiskSummaryLine({
            patternTitle: currentTitle,
            zoneLabel: groupedUnderstandRiskLocationLine.split(' · ')[0] || groupedScopeSummary.split(' · ')[0],
            fallbackSummary: groupedWhatToKnow,
          })}
          frequencyLine={pattern.patternVolumeDisplay}
          locationLine={groupedUnderstandRiskLocationLine || groupedScopeSummary}
          statusLine={[
            displayPatternWorkflowLabel(workflowLabel || 'Needs review') || 'Needs review',
            displayPatternEvidenceProofLabel(groupedEvidenceStatusLabel) || 'Not evaluated',
          ].join(' · ')}
          contextLine={[
            areaBehaviorSummary ? `Area: ${areaBehaviorSummary}` : '',
            sensitivitySummary ? `Sensitivity: ${sensitivitySummary}` : '',
            (policyContextValue('Policy version') || policyContextValue('Version'))
              ? `Policy ${policyContextValue('Policy version') || policyContextValue('Version')}`
              : '',
          ].filter(Boolean).join(' · ')}
        />
      )}
      recurringRiskSummary={buildRecurringRiskSummaryLine({
        patternTitle: currentTitle,
        zoneLabel: groupedUnderstandRiskLocationLine.split(' · ')[0] || groupedScopeSummary.split(' · ')[0],
        fallbackSummary: groupedWhatToKnow,
      })}
      patternVolumeCount={reviewWindowDisplayCount}
      patternVolumeKind={patternVolumeKind}
      reviewWindowContext={reviewWindowContext}
      locationLine={groupedUnderstandRiskLocationLine || groupedScopeSummary}
      policyLabel={groupedPolicyLabel}
      patternOwner={groupedReviewModel.representative.currentPatternOwner}
      followUpOwner={followUpOwnerSummary}
      followUpStatus={followUpStateSummary}
      workflowStatus={String(selectedIncident?.status || '')}
      nextReviewSummary={groupedReviewModel.groupedNextReviewSummary}
      workOrderSummary={workOrderSummary}
      patternDecisionCard={groupedPatternDecisionCard}
      heatmapSection={groupedHeatmapSection}
    />
  ) : null;
  const renderStepNavigation = (stepId: JourneyStepId) => {
    if (groupedReviewMode || observationDispositionMode) return null;
    return (
      <IncidentDrawerStepNavigation
        stepId={stepId}
        journeyStepIds={journeyStepIds}
        journeySteps={journeySteps}
        completedJourneySteps={completedJourneySteps}
        journeyStateMeta={journeyStateMeta}
        onToggleJourneyStep={handleToggleJourneyStep}
        onScrollToJourneyStep={scrollToJourneyStep}
      />
    );
  };

  const showGroupedPageTitleChrome = isPageMode && groupedReviewMode;

  const detailScope: IncidentDrawerDetailScope = {
    IncidentEvidenceAttachmentPreview,
    PolicyNotificationStatusChip,
    _bestSnapshotAttachment,
    _bestVideoAttachment,
    _edgeSuppressions,
    _edgeSuppressionsByDevice,
    _fetchData,
    _findingForIncident,
    _handleRunIncidentRunbook,
    _handleSaveSeverity,
    _humanLabelCompact,
    _incidentTypeLabel,
    _isAdvancedIncidentsUi,
    _loadEdgeSuppressions,
    _loadPpeMissingZoneDiag,
    _openReportDialog,
    _opsStateLabel,
    _overlaySnapshotBlob,
    _ppeItemsSummary,
    _ppeMissingItemsFromIncident,
    _ppeSnapshotItemsFromIncident,
    _ppeStabilizedStateFromIncident,
    _refreshSelectedIncident,
    _setCloseReason,
    _setFilterOpsQueue,
    _setFilterSeverity,
    _severityChipSx,
    _severityIcon,
    _titleCase,
    actionScopeSummary,
    activeJourneyPhase,
    activeJourneyStep,
    activeScope,
    affectedEntitySummary,
    alertTriggeredTimeMs,
    allPolicies,
    allZones,
    areaBehaviorSummary,
    attachmentCount,
    attentionReasonSummary,
    buildContextBundle,
    camN,
    cameraNameById,
    canAdjustIncidentThreshold,
    canCommentOnIncident,
    canExportIncidentEvidence,
    canManageGroupPatterns,
    canManageIncidentSuppressions,
    canManageIncidentWorkflow,
    canOpenAuditTrail,
    canOpenCopilot,
    canOverrideIncidentSeverity,
    canRunIncidentRunbooks,
    canUseOpsAutomation,
    captureLeadMs,
    captureRequestedTimeMs,
    cardHeadingSx,
    cardLabelSx,
    clipAttachmentMeta,
    clipTiming,
    closeoutCommentState,
    closeoutEvidenceState,
    closeoutGuidance,
    closeoutHistoryState,
    commentInputRef,
    completedJourneySteps,
    confidenceSummary,
    contextChips,
    currentPolicyRecord,
    currentTitle,
    degradationSummary,
    deriveLifecycle,
    derivePolicyNotificationRoutes,
    detectionFindingSummary,
    detectionRuleSummary,
    detectionSystemIssueSummary,
    downloadBlob,
    drawerAdvancedOpen,
    drawerBusy,
    drawerCardSx,
    drawerIncidentLoading,
    drawerScrollRef,
    drawerStep0Ref,
    drawerStep1Ref,
    drawerStep2Ref,
    drawerStep3Ref,
    drawerStep4Ref,
    drawerStep5Ref,
    drawerStep6Ref,
    drawerStep7Ref,
    drawerStep8Ref,
    edgeDeliveryState,
    edgeIncidentOutbox,
    edgeLiveSummary,
    edgeOutboxSummary,
    edgeOutboxSummaryError,
    edgeOutboxSummaryLoading,
    effectiveClipStatus,
    effectiveMetaStatus,
    effectiveSnapshotStatus,
    emailTargetDraft,
    eventLeadMs,
    eventStartedTimeMs,
    evidenceAnchorTimeMs,
    evidenceAttachmentByFilename,
    evidenceAttachments,
    evidenceCount,
    evidenceLatest,
    evidenceOutboxStats,
    evidenceRetryWaitTop,
    evidenceTargetTimeMs,
    evidenceTimingAvailable,
    executeRunbookServer,
    fetchEvidenceFileBlob,
    fetchLatestSnapshotBlob,
    fetchLatestVideoBlob,
    followUpMarkedComplete,
    followUpOwnerSummary,
    followUpSignal,
    followUpState,
    followUpStateSummary,
    formatAgeSeconds,
    formatTimingDelta,
    formatTimingInstant,
    getCurrentScrollTop,
    getJourneyPhaseState,
    getJourneyStepState,
    getJourneyStepTop,
    getOdooStateColor,
    groupPatternActionBusy,
    groupedDeepReviewOpen,
    groupedEvidenceStatusLabel,
    groupedFirstSeenLabel,
    groupedHeatmapBucketCount,
    groupedHeatmapCard,
    groupedHeatmapCellSelectCaption,
    groupedHeatmapSummaryText,
    groupedIncidentExplanation,
    groupedLastSeenLabel,
    groupedPolicyLabel,
    groupedProgressSummary,
    groupedReasonText,
    groupedReviewLead,
    workflowReviewContextLead,
    groupedReviewMode,
    observationDispositionMode,
    groupedReviewModel,
    groupedScopeSummary,
    groupedPrimaryLocationLine,
    groupedSeriesSummaryText,
    groupedSummaryText,
    groupedWhatToKnow,
    groupedWorkflowProgress,
    groupedLocalProgressHint,
    groupedGuidedInitialPanelId,
    handleClearGroupedLocalProgress,
    handleGroupedGuidedExpandedPanelChange,
    groupedRepresentativeReviewComplete,
    groupedRepresentativeReviewOutcome,
    groupedEvidenceConfirmed,
    groupedCorrectiveActionIsLocked,
    groupedCorrectiveActionPlanSaved,
    groupedCorrectiveWorkflowPhase,
    groupedCorrectivePlanDraftDirty,
    groupedTrackImpactReady,
    groupedFollowUpStarted,
    groupedFollowUpMeasurementRecorded,
    groupedReportSignOffMissingItems,
    groupedReportBlocked,
    groupedReportBlockedReason,
    handleGroupedGuidedPrimaryAction,
    handleGroupedGuidedSecondaryAction,
    focusGroupedGuidedPanel,
    registerGroupedGuidedPanelControl,
    groupedAssignActionPanel,
    openResolveDialogWithOutcome,
    resolveTargetIncidentId: representativeIncidentId,
    groupedPatternDecisionCard,
    groupedHeatmapSection,
    groupedUnderstandRiskPanel,
    riskCategoryReportLink,
    refreshWorkflowCorrectiveSummary,
    handleAcknowledge,
    handleAddCommentShortcut,
    handleClearSuppressionForIncident,
    handleEnsureWorkOrder,
    handleExportEvidence,
    handleHeatmapCellSelect,
    handleMarkFalsePositive,
    handleMarkInReview,
    handleOpenPolicyEditor,
    handleOpenThresholdTuning,
    handleOverrideSeverity,
    handleSaveMeasurement,
    handleSaveCorrectiveAction,
    handleSaveCoverageReviewAttestation,
    handleSaveZoneControlsConfirmation,
    handleAddWorkflowEvidenceNote,
    handleSaveNote,
    handleSaveNotificationTargets,
    handleSaveOps,
    handleSaveFollowupOwner,
    handleUpdateWorkflowActionStatus,
    handleSyncFromOdoo,
    handleSyncToOdoo,
    handleToggleJourneyStep,
    hasClipArtifact,
    hasSnapshotArtifact,
    heatmapCacheRef,
    heatmapData,
    heatmapError,
    heatmapLoading,
    heatmapOpening,
    heatmapPageFeedback,
    heatmapQuery,
    heatmapRequestKey,
    heatmapViewingIncidentId,
    incidentActivityError,
    incidentActivityItems,
    incidentActivityLoading,
    incidentComments,
    incidentCommentsError,
    incidentCommentsLoading,
    incidentSafetyEpisodes,
    incidentSafetyEpisodesAvailable,
    incidentSafetyEpisodesError,
    incidentSafetyEpisodesLoading,
    incidentCreateLagMs,
    incidentCreatedAtMs,
    incidentOutboxQueuedCount,
    incidentOutboxStats,
    incidentRetryWaitTop,
    incidentRowTypeLabel,
    incidentRunbookBusy,
    incidentTypeKey,
    inferredAlertTriggeredTimeMs,
    isPageMode,
    isPpeIncident,
    journeyEvidenceFingerprint,
    journeyPhaseForStep,
    journeyPhaseMeta,
    journeyPhaseTitleForStep,
    journeyPhases,
    journeyProgressStorageKey,
    journeyRailRef,
    journeyStateMeta,
    journeyStepIds,
    journeySteps,
    latestSnapshotError,
    latestSnapshotLoading,
    latestSnapshotObjectUrl,
    latestVideoAttachment,
    latestVideoError,
    latestVideoIdentity,
    latestVideoLoading,
    latestVideoObjectUrl,
    evidenceMomentError,
    evidencePlayerCardRef,
    evidencePlayerRef,
    handleEvidencePlayerError,
    handleEvidencePlayerLoadedMetadata: seekLoadedEvidenceMoment,
    handleViewEvidenceMoment,
    matchingSuppressions,
    measurementBaselineDraft,
    measurementFollowUpDraft,
    measurementMeasuredAtDraft,
    measurementNoteDraft,
    measurementImpactWaivedDraft,
    measurementOutcomeStatusDraft,
    correctiveActionTypeDraft,
    correctiveActionPriorityDraft,
    correctiveActionDueDateDraft,
    correctiveActionVerificationMethodDraft,
    correctiveActionControlReviewDraft,
    workflowId,
    safetyCaseId: riskPatternWorkflowReviewContext?.safetyCaseId || '',
    hasLinkedSafetyCase,
    pendingObservationVerificationCount,
    riskPatternWorkflowReviewActions,
    workflowCorrectiveActions,
    workflowImpactSummary,
    workflowClaimReadiness,
    workflowFieldVerification,
    fieldVerificationBusy,
    handleAnswerFieldVerificationItem,
    claimReadinessRefreshKey,
    navigate,
    nextIncompleteJourneyPhase,
    nextIncompleteJourneyStep,
    nextReviewAtValue,
    nextReviewRelativeSummary,
    nextReviewSummary,
    noteDraft,
    notificationActionValue,
    notificationEditorBusy,
    notificationEditorDeployNow,
    notificationEditorError,
    notificationEditorOpen,
    onGroupPatternAction,
    onGroupedHeatmapCellSelect,
    onHeatmapWindowChange,
    onOpenReviewPage,
    onOpenReviewPageFullProof,
    onViewPatternOccurrences,
    oneLineSummary,
    openAuditTrail,
    openDeepReviewChecklist,
    openDiagnostics,
    openRelatedIncidents,
    openRiskAnalytics,
    opsAssigneeDraft,
    opsSlaTargetDraft,
    opsStateDraft,
    opsStateLabel,
    opsStateResult,
    opsTeamDraft,
    packPolicyHeader,
    parseEvidenceFilenameMs,
    parseTimingMs,
    patternActionAvailabilityMessage,
    policiesForPresentation,
    policyContext,
    policyContextValue,
    policyScopeForEditing,
    ppeMissingZoneDiag,
    ppeMissingZoneDiagError,
    ppeMissingZoneDiagLoading,
    proofSummary,
    patternVolumeCount,
    patternVolumeKind,
    rawAlertTriggeredTimeMs,
    recurringIssueSummary,
    registerOccurrenceOpenedHandler,
    relatedEventSignalsSummary,
    relatedIncidentCount,
    renderAuditStepHeading,
    renderMode,
    renderStepNavigation,
    representativeIncidentId,
    representativeIncidentSummary,
    reviewFocusSummary,
    reviewJourneySessionId,
    reviewShellRef,
    reviewTimeRange,
    reviewExactMode,
    reviewHeatmapHourActive,
    suppressAutoBetterProof:
      groupedExpandedPanelId === 'decide_action'
      || groupedExpandedPanelId === 'track_impact'
      || groupedExpandedPanelId === 'report',
    workflowFeedback,
    reviewerActionPanel,
    reviewingLatestInSeries,
    riskPatternLabel,
    riskPatternSummary,
    ruleHelpAnchorEl,
    runbookTemplates,
    safeText,
    safeWindowScrollTo,
    scopedSiteId,
    scrollToJourneyPhase,
    scrollToJourneyStep,
    scrollToPatternDecision,
    selectedCameraName,
    selectedGroupCameraName,
    selectedGroupContext,
    selectedGroupKey,
    selectedGroupZoneName,
    selectedIncident,
    selectedZoneName,
    sensitivitySummary,
    seriesActionLabel,
    seriesContextSummary,
    setActiveJourneyStep,
    setCloseDialogOpen,
    setCompletedJourneySteps,
    setDrawerAdvancedOpen,
    setEdgeIncidentOutbox,
    setEdgeOutboxSummary,
    setEdgeOutboxSummaryError,
    setEdgeOutboxSummaryLoading,
    setEmailTargetDraft,
    setFilterCameraId,
    setFilterDeviceId,
    setFilterIncidentType,
    setFilterZoneId,
    setGroupedDeepReviewOpen,
    setHeatmapData,
    setHeatmapError,
    setHeatmapLoading,
    setIncidentRunbookBusy,
    setMeasurementBaselineDraft,
    setMeasurementFollowUpDraft,
    setMeasurementMeasuredAtDraft,
    setMeasurementNoteDraft,
    setMeasurementImpactWaivedDraft,
    setMeasurementOutcomeStatusDraft,
    setCorrectiveActionTypeDraft,
    setCorrectiveActionPriorityDraft,
    setCorrectiveActionDueDateDraft,
    setCorrectiveActionVerificationMethodDraft,
    setCorrectiveActionControlReviewDraft,
    setNoteDraft,
    setNotificationEditorBusy,
    setNotificationEditorDeployNow,
    setNotificationEditorError,
    setNotificationEditorOpen,
    setOpsAssigneeDraft,
    setOpsSlaTargetDraft,
    setOpsStateDraft,
    setOpsTeamDraft,
    setRuleHelpAnchorEl,
    setSeverityDraft,
    setSmsTargetDraft,
    setSuppressDialogOpen,
    setTableMode,
    setViewDialogOpen,
    setWebhookTargetDraft,
    severityDraft,
    showGroupedPageTitleChrome,
    showSeparateAlertMoment,
    smsTargetDraft,
    snapshotAttachment,
    strictReviewerImagePolicy,
    snapshotAttachmentMeta,
    snapshotCapturedAtMs,
    snapshotCloudReceivedAtMs,
    snapshotDeltaMs,
    snapshotFrameTimeMs,
    snapshotLooksDelayed,
    reviewerAidSnapshotRef,
    sourceTruthSnapshotRef,
    sourceTruthClipRef,
    primaryReviewerImageLabel,
    secondaryEvidenceLabel,
    reviewerAidBboxStatusLabel,
    incidentSubjectLabel,
    incidentSubjectMetadataChipLabel,
    incidentSubjectConfidence,
    forkliftSubjectMetadataChipLabel: forkliftSubjectChipLabel,
    forkliftConfidenceLabel,
    triggerVerificationLabel,
    triggerVerificationStatus,
    snapshotConfidenceLabel,
    stepSectionSx,
    summaryExplanation,
    suppressionsError,
    suppressionsLoading,
    targetSummary,
    titleCaseWords,
    user,
    viewDialogOpen,
    webhookTargetDraft,
    workOrderSummary,
    workflowIsClosed,
    workflowLabel,
    workflowStatus,
    zoneN,
    zoneNameById,
    zonesByIdForPack,
    siteTimeZone,
  };
  const detailContent = <IncidentDrawerDetailContent scope={detailScope} />;

  if (isPageMode) {
    return (
      <Box
        ref={reviewShellRef}
        data-testid="incident-review-page-shell"
        sx={{
          p: { xs: 1.6, md: 2.4 },
          borderRadius: 3,
          bgcolor: '#f4f7fb',
          boxShadow: '0 20px 44px rgba(15, 23, 42, 0.12)',
          border: '1px solid',
          borderColor: '#d6deeb',
        }}
      >
        {detailContent}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={viewDialogOpen}
      onClose={() => setViewDialogOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 720 },
          maxWidth: '100%',
          p: 2.1,
          bgcolor: '#f4f7fb',
          boxShadow: '-18px 0 48px rgba(15, 23, 42, 0.18)',
        },
      }}
    >
      {detailContent}
    </Drawer>
  );
};

export default IncidentDrawer;
